import React, { useState, useEffect, useCallback, useMemo } from "react";
import API_BASE_URL from "./RestAPI";
import { showSuccessToast, showErrorToast } from "../constants/ToastService.js";
import "./SprintDashboard.css";
import "./SharedTable.css";
import "./Sprints.css";
import TicketSidePanel from "./TicketPanel.js";
import { FiEdit2, FiTrash2, FiEye, FiPlus, FiChevronDown, FiChevronRight, FiPaperclip, FiDownload, FiGitBranch, FiRefreshCw, FiSearch, FiFilter, FiShare2, FiX, FiUser, FiUsers, FiClock, FiSave, FiXCircle, FiFileText, FiGitMerge, FiGitCommit, FiCheck, FiZap, FiLink, FiUploadCloud, FiRotateCcw } from "react-icons/fi";
import { GoBug } from "react-icons/go";
import { formatDate } from "./Common Components/CommonFunction.js";
import FilterDropdown from "./Common Components/FilterDropdown";
import useCurrentProject, { resolveProject } from "./Common Components/useCurrentProject";
import {
  Autocomplete, Button, CircularProgress, Menu, MenuItem, TextField, Tooltip, Modal, Box, Typography, IconButton,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DetailedTicketView from "./DetailedTicketView.js";
import LiveMovePanel from "./LiveMovePanel.js";

const SPRINT_STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Ongoing", value: "ONGOING" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Overdue", value: "OVERDUE" },
  { label: "Cancelled", value: "CANCELLED" },
];

const STATUS_PILL_CLASS = {
  ONGOING: "sprint-status-pill--ongoing",
  COMPLETED: "sprint-status-pill--completed",
  OVERDUE: "sprint-status-pill--overdue",
  CANCELLED: "sprint-status-pill--cancelled",
};

/* ── Donut Chart (pure CSS conic-gradient) ── */
const DonutChart = ({ segments, total }) => {
  let gradient = "";
  let offset = 0;
  const colors = { green: "var(--status-green)", blue: "var(--status-blue)", yellow: "#f5a623", red: "var(--status-red)", gray: "var(--text-subtle)" };

  if (total === 0) {
    gradient = `${colors.gray} 0deg 360deg`;
  } else {
    const parts = [];
    segments.forEach(({ count, color }) => {
      if (count > 0) {
        const deg = (count / total) * 360;
        parts.push(`${colors[color] || colors.gray} ${offset}deg ${offset + deg}deg`);
        offset += deg;
      }
    });
    if (offset < 360) parts.push(`var(--bg-tertiary) ${offset}deg 360deg`);
    gradient = parts.join(", ");
  }

  return (
    <div className="sprint-donut" style={{ background: `conic-gradient(${gradient})` }}>
      <div className="sprint-donut__center">
        <span className="sprint-donut__number">{total}</span>
        <span className="sprint-donut__label">tickets</span>
      </div>
    </div>
  );
};

/* ── KPI Bar ── */
const KPIBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="sprint-kpi-bar-row">
      <span className="sprint-kpi-bar-label">{label}</span>
      <div className="sprint-kpi-bar-track">
        <div className={`sprint-kpi-bar-fill sprint-kpi-bar-fill--${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="sprint-kpi-bar-count">{count}</span>
    </div>
  );
};

/* ── Due date ── */
/**
 * Classify a due date relative to today: 'overdue' (past), 'soon' (today or
 * tomorrow), or 'normal'. Compared at day precision so a date due later today
 * never reads as overdue.
 */
const dueState = (raw) => {
  if (!raw) return null;
  const due = new Date(raw);
  if (Number.isNaN(due.getTime())) return null;
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((due - today) / 86400000);
  if (days < 0) return 'overdue';
  if (days <= 1) return 'soon';
  return 'normal';
};

const DueDateCell = ({ value }) => {
  const state = dueState(value);
  if (!state) return <span className="sl2-no-assign">-</span>;
  const label = new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  const title = state === 'overdue' ? 'Overdue' : state === 'soon' ? 'Due today / tomorrow' : '';
  return <span className={`sl2-due sl2-due--${state}`} title={title}>{label}</span>;
};

/* ═══════════════ MAIN COMPONENT ═══════════════ */
const SprintDashboard = () => {
  const userPermission = JSON.parse(localStorage.getItem("userPermissions") || "[]");

  // ── Core state ──
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useCurrentProject();
  const [sprintStatusFilter, setSprintStatusFilter] = useState("ONGOING");
  const [kpiData, setKpiData] = useState([]);
  const [kpiLoading, setKpiLoading] = useState(false);

  // ── Level 2: Selected sprint + tickets ──
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [ticketList, setTicketList] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketSearch, setTicketSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterAssigned, setFilterAssigned] = useState("");
  const [filterType, setFilterType] = useState("");
  const [dueSort, setDueSort] = useState(""); // '' | 'asc' | 'desc'
  const [showFilters, setShowFilters] = useState(true);
  const [expandedParents, setExpandedParents] = useState({});
  const [bugCount, setBugCount] = useState(0);
  const [ticketCount, setTicketCount] = useState(0);

  // ── Level 3: Selected ticket workspace ──
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketComments, setTicketComments] = useState([]);
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkName, setLinkName] = useState("");
  const fileInputRef = React.useRef(null);
  const [ticketBranches, setTicketBranches] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [branchSyncing, setBranchSyncing] = useState(null);
  const [detailTab, setDetailTab] = useState('Details');
  const [parentTicketStack, setParentTicketStack] = useState([]);
  const [showAddBranch, setShowAddBranch] = useState(false);
  const [addBranchForm, setAddBranchForm] = useState({ repository_id: '', branch_name: '', developer_id: '', branch_type: 'feature' });
  const [gitlabRepos, setGitlabRepos] = useState([]);

  // ── Side panel (existing edit flow) ──
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // ── Sprint CRUD ──
  const [showSprintPopup, setShowSprintPopup] = useState(false);
  const [sprintForm, setSprintForm] = useState({ sprint_name: "", short_name: "", start_date: "", end_date: "", team_name: "", team_members: [], is_live_support: false });
  const [editSprintId, setEditSprintId] = useState(null);
  const [employees, setEmployees] = useState([]);
  // Live Support Sprint feature flag (Django admin → Configuration.enable_live_support_sprint)
  const [liveSupportEnabled, setLiveSupportEnabled] = useState(
    JSON.parse(localStorage.getItem("liveSupportEnabled") || "false")
  );

  // ── Context menu ──
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuTicket, setMenuTicket] = useState(null);

  // ── Detailed view modal ──
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [detailViewTicket, setDetailViewTicket] = useState(null);

  // ── Inline edit state ──
  const [editRowId, setEditRowId] = useState(null);
  const [editedValues, setEditedValues] = useState({ short_description: "", ticket_status: "", assigned_to_name: "" });
  const [ticketStatuses, setTicketStatuses] = useState([]);

  // ── Move to sprint ──
  const [movePopup, setMovePopup] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [allSprints, setAllSprints] = useState([]);

  // ── Add child/bug ticket ──
  const [addTicketPopup, setAddTicketPopup] = useState(false);
  const [addTicketParent, setAddTicketParent] = useState(null);
  const [addTicketType, setAddTicketType] = useState(""); // 'Story' or 'Bug'
  const [addTicketForm, setAddTicketForm] = useState({ short_description: "", status_id: "", priority_id: "", bug_type: "Development" });
  const [priorityTypes, setPriorityTypes] = useState([]);

  const [refreshTrigger, setRefreshTrigger] = useState(false);

  // ── Level 1: Per-sprint developer performance ──
  const [sprintDevData, setSprintDevData] = useState({});

  // ── Inline status edit (Level 2 table) ──
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusDropPos, setStatusDropPos] = useState({ top: 0, left: 0 });

  const openStatusDrop = (e, ticketId) => {
    e.stopPropagation();
    if (editingStatusId === ticketId) { setEditingStatusId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < 260 ? rect.top - Math.min(260, rect.top - 10) : rect.bottom + 4;
    setStatusDropPos({ top, left: rect.left });
    setEditingStatusId(ticketId);
  };

  // Close status dropdown on outside click
  useEffect(() => {
    if (!editingStatusId) return;
    const handler = () => setEditingStatusId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [editingStatusId]);

  // ── Quick Create Ticket (Level 2) ──
  // Direct parent ticket creation inside a sprint is blocked — parent
  // Epic/Story points must be moved in from Client Requirements.

  // ── Inline editing (Level 3) ──
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [editedTicket, setEditedTicket] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [expandedBranchId, setExpandedBranchId] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);

  // ── Move back to Client Requirements (Level 3) ──
  const [moveBackTicket, setMoveBackTicket] = useState(null); // ticket obj or null
  const [moveBackClientId, setMoveBackClientId] = useState('');
  const [moveBackStatusId, setMoveBackStatusId] = useState('');
  const [moveBackSaving, setMoveBackSaving] = useState(false);
  const [crClients, setCrClients] = useState([]);
  const [crStatuses, setCrStatuses] = useState([]);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("authToken")}` };

  // Opens the move-back dialog. Unmapped tickets need a Client (and optional
  // status) for the new requirement, so load those lists lazily.
  const openMoveBack = async (ticket) => {
    setMoveBackTicket(ticket);
    setMoveBackClientId('');
    setMoveBackStatusId('');
    if (!ticket.client_requirement_id) {
      try {
        const [cRes, sRes] = await Promise.all([
          fetch(`${API_BASE_URL}client_requirements/client/`, { headers }),
          fetch(`${API_BASE_URL}client_requirements/clientstatus/`, { headers }),
        ]);
        const cData = await cRes.json();
        const sData = await sRes.json();
        if (cData.status === 'success') setCrClients(cData.data || []);
        if (sData.status === 'success') setCrStatuses(sData.data || []);
      } catch (e) { console.error(e); }
    }
  };

  const handleMoveBack = async () => {
    const ticket = moveBackTicket;
    if (!ticket) return;
    if (!ticket.client_requirement_id && !moveBackClientId) {
      showErrorToast('Please select a client for the new requirement');
      return;
    }
    setMoveBackSaving(true);
    try {
      // Mapped ticket → revoke the sprint move (ticket detached + reused later).
      // Unmapped ticket → create a new Client Requirement from the ticket.
      const res = ticket.client_requirement_id
        ? await fetch(`${API_BASE_URL}client_requirements/requirements/revoke-sprint/`, {
            method: 'POST', headers,
            body: JSON.stringify({ id: ticket.client_requirement_id }),
          })
        : await fetch(`${API_BASE_URL}client_requirements/requirements/from-ticket/`, {
            method: 'POST', headers,
            body: JSON.stringify({
              ticket_id: ticket.id,
              client_id: moveBackClientId,
              ...(moveBackStatusId ? { status_id: moveBackStatusId } : {}),
            }),
          });
      const data = await res.json();
      if (data.status === 'success') {
        showSuccessToast(data.message || 'Moved back to Client Requirements');
        setMoveBackTicket(null);
        setSelectedTicket(null);
        setRefreshTrigger(p => !p);
      } else {
        showErrorToast(data.message || 'Move back failed');
      }
    } catch (e) { console.error(e); showErrorToast('Move back failed'); }
    finally { setMoveBackSaving(false); }
  };

  // Fetch the company feature config so the Live Support Sprint checkbox shows
  // only when enabled in Django admin.
  useEffect(() => {
    fetch(`${API_BASE_URL}core/feature-config/`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          const enabled = Boolean(data.data?.live_support_sprint);
          setLiveSupportEnabled(enabled);
          localStorage.setItem("liveSupportEnabled", JSON.stringify(enabled));
        }
      })
      .catch((err) => console.error("Failed to load feature config:", err));
  }, []);

  // ══════════ DATA FETCHING ══════════

  const fetchProjects = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}core/project/`, { headers });
      const data = await res.json();
      if (data.status === "success") {
        setProjects(data.data);
        setCurrentProject(prev => resolveProject(data.data, prev));
      }
    } catch (e) { console.error(e); }
  }, []);

  const fetchKPI = useCallback(async (projectId, statusFilter) => {
    if (!projectId) return;
    setKpiLoading(true);
    try {
      const statusParam = statusFilter === "all" ? "" : `&status=${statusFilter}`;
      const res = await fetch(`${API_BASE_URL}ticketing/sprint/kpi/?project_id=${projectId}${statusParam}`, { headers });
      const data = await res.json();
      if (data.status === "success") setKpiData(data.data);
      else showErrorToast(data.message);
    } catch (e) { console.error(e); showErrorToast("Failed to load sprints"); }
    finally { setKpiLoading(false); }
  }, []);

  const fetchTickets = useCallback(async (sprintId) => {
    if (!sprintId) return;
    setTicketLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/?sprint_id=${sprintId}`, { headers });
      const data = await res.json();
      if (data.status === "success") {
        const tickets = Array.isArray(data.data) ? data.data : [];
        setTicketList(tickets);
        setBugCount(data.bug_counts || 0);
        setTicketCount(data.total_tickets || 0);
        // Helper to find a ticket by id in the refreshed data
        const findTicketById = (id) => {
          for (const t of tickets) {
            if (t.id === id) return t;
            if (t.child_tickets) {
              for (const c of t.child_tickets) {
                if (c.id === id) return c;
                if (c.bug_tickets) {
                  for (const b of c.bug_tickets) { if (b.id === id) return b; }
                }
              }
            }
            if (t.bug_tickets) {
              for (const b of t.bug_tickets) { if (b.id === id) return b; }
            }
          }
          return null;
        };
        // Re-sync selectedTicket with fresh data
        setSelectedTicket(prev => {
          if (!prev) return prev;
          return findTicketById(prev.id) || prev;
        });
        // Re-sync parentTicketStack with fresh data
        setParentTicketStack(prev => prev.map(entry => {
          const fresh = findTicketById(entry.ticket.id);
          return fresh ? { ...entry, ticket: fresh } : entry;
        }));
      } else showErrorToast(data.message);
    } catch (e) { console.error(e); }
    finally { setTicketLoading(false); }
  }, []);

  const fetchComments = useCallback(async (ticketId) => {
    if (!ticketId) return;
    setCommentsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/comments/?ticket_id=${ticketId}`, { headers });
      const data = await res.json();
      if (data.status === "success") setTicketComments(data.data || []);
    } catch (e) { console.error(e); }
    finally { setCommentsLoading(false); }
  }, []);

  const fetchBranches = useCallback(async (ticketId) => {
    if (!ticketId) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/gitlab/branches/?ticket_id=${ticketId}`, { headers });
      const data = await res.json();
      if (data.status === "success") setTicketBranches(data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchGitlabRepos = useCallback(async (projectId) => {
    if (!projectId) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/gitlab/repos/?project_id=${projectId}`, { headers });
      const data = await res.json();
      if (data.status === "success") setGitlabRepos(data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchAttachments = useCallback(async (ticketId) => {
    if (!ticketId) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/attachements/?ticket_id=${ticketId}`, { headers });
      const data = await res.json();
      if (data.status === "success") setTicketAttachments(data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}core/employee/`, { headers });
      const data = await res.json();
      if (data.status === "success") setEmployees(data.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchTicketStatuses = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}core/status/`, { headers });
      const data = await res.json();
      if (data.status === "success") setTicketStatuses(data.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchPriorityTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}core/priorityType/`, { headers });
      const data = await res.json();
      if (data.status === "success") setPriorityTypes(data.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchTicketTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}core/ticketType/`, { headers });
      const data = await res.json();
      if (data.status === "success") setTicketTypes(data.data);
    } catch (e) { console.error(e); }
  }, []);

  // ── Move-to-Production guard ───────────────────────────────────────────
  // A ticket can only be moved to the "Production" status after its move-to-live
  // request is approved. We block it on the client (with an alert) so the user
  // gets immediate feedback; the backend enforces the same rule authoritatively.
  const isProductionStatus = (statusId) => {
    const s = ticketStatuses.find((st) => String(st.id) === String(statusId));
    const name = (s?.ticket_status || '').trim();
    return /production/i.test(name) || /^live$/i.test(name);
  };

  const liveMoveApproved = async (ticketId) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/live-move/?ticket_id=${ticketId}`, {
        method: 'GET', headers,
      });
      const data = await res.json();
      return data?.data?.latest?.request_status === 'APPROVED';
    } catch {
      return false;
    }
  };

  const ensureStatusAllowed = async (ticketId, newStatusId) => {
    const featureOn =
      JSON.parse(localStorage.getItem('releaseApprovalEnabled') || 'false') ||
      JSON.parse(localStorage.getItem('liveSupportEnabled') || 'false');
    if (!featureOn || !isProductionStatus(newStatusId)) return true;
    const approved = await liveMoveApproved(ticketId);
    if (!approved) {
      showErrorToast('This ticket cannot be moved to Production until its move-to-live request is approved.');
      return false;
    }
    return true;
  };

  const handleInlineSave = async () => {
    if (!editedTicket) return;
    const newStatusId = editedTicket.status_id || editedTicket.status;
    if (!(await ensureStatusAllowed(editedTicket.id, newStatusId))) return;
    setEditSaving(true);
    try {
      const payload = {
        id: editedTicket.id,
        short_description: editedTicket.short_description,
        detailed_description: editedTicket.detailed_description || '',
        user_story: editedTicket.user_story || '',
        acceptance_criteria: editedTicket.acceptance_criteria || '',
        status_id: editedTicket.status_id || editedTicket.status,
        priority_id: editedTicket.priority_id || editedTicket.priority,
        ticket_type: editedTicket.ticket_type,
        assigned_to_id: editedTicket.assignee_id || editedTicket.assignee || null,
        sprint_id: editedTicket.sprint_id,
        company_id: 1,
        developers: (editedTicket.developers || []).map(d => ({ id: d.id, name: d.name || d })),
        qa_id: editedTicket.qa_id || editedTicket.qa || null,
        peer_qa_id: editedTicket.peer_qa_id || editedTicket.peer_qa || null,
        uat_id: editedTicket.uat_id || editedTicket.uat || null,
        dev_est_time: editedTicket.dev_est_time || '',
        qa_est_time: editedTicket.qa_est_time || '',
        story_points: editedTicket.story_points || '',
        // Always sent (even empty) so clearing the date actually unsets it.
        due_date: editedTicket.due_date || '',
      };
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/`, {
        method: 'PUT', headers, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.status === 'success') {
        showSuccessToast('Ticket updated');
        setIsInlineEditing(false);
        setEditedTicket(null);
        setRefreshTrigger(p => !p);
      } else showErrorToast(data.message || 'Update failed');
    } catch (e) { console.error(e); showErrorToast('Update failed'); }
    finally { setEditSaving(false); }
  };

  const handleInlineStatusChange = async (ticket, newStatusId) => {
    setEditingStatusId(null);
    if (!(await ensureStatusAllowed(ticket.id, newStatusId))) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/`, {
        method: 'PUT', headers,
        body: JSON.stringify({ id: ticket.id, sprint_id: ticket.sprint_id, status_id: newStatusId, company_id: 1 }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        showSuccessToast('Status updated');
        setRefreshTrigger(p => !p);
      } else showErrorToast(data.message || 'Update failed');
    } catch (e) { showErrorToast('Status update failed'); }
  };

  const fetchAllSprints = useCallback(async (projectId) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/sprint/?project_id=${projectId}`, { headers });
      const data = await res.json();
      if (data.status === "success") setAllSprints(data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  // ══════════ EFFECTS ══════════

  useEffect(() => { fetchProjects(); fetchEmployees(); fetchTicketStatuses(); fetchPriorityTypes(); fetchTicketTypes(); }, []);
  useEffect(() => { if (currentProject) { fetchKPI(currentProject, sprintStatusFilter); fetchAllSprints(currentProject); } }, [currentProject, sprintStatusFilter, refreshTrigger]);
  useEffect(() => { if (selectedSprint) fetchTickets(selectedSprint.id); }, [selectedSprint, refreshTrigger]);
  useEffect(() => {
    if (selectedTicket) { fetchComments(selectedTicket.id); fetchAttachments(selectedTicket.id); fetchBranches(selectedTicket.id); }
    else { setTicketComments([]); setTicketAttachments([]); setTicketBranches([]); }
  }, [selectedTicket]);
  useEffect(() => { if (currentProject) fetchGitlabRepos(currentProject); }, [currentProject]);

  // Fetch dev analysis for all visible sprints (Level 1 performance)
  useEffect(() => {
    if (!kpiData || kpiData.length === 0) return;
    const fetchDevForSprints = async () => {
      const results = {};
      await Promise.allSettled(kpiData.map(async (sp) => {
        try {
          const res = await fetch(`${API_BASE_URL}reports/sprint/analysis/?sprint_id=${sp.id}`, { method: 'GET', headers });
          const data = await res.json();
          if (data.status === 'success' && Array.isArray(data.data)) {
            const devMap = {};
            // Story + Epic only — bugs are surfaced via the separate Bugs KPI
            data.data.filter(t => t.ticket_type !== 'Bug').forEach(t => {
              const devName = t.developer_names || '';
              const name = typeof devName === 'string' ? devName.split(',')[0]?.trim() : '';
              if (!name) return;
              if (!devMap[name]) devMap[name] = { tickets: 0, done: 0, inProg: 0, bugs: 0, devHrs: 0 };
              devMap[name].tickets += 1;
              devMap[name].devHrs += parseFloat(t.dev_time_took || t.dev_time_taken || 0);
              const st = (t.ticket_status || '').toLowerCase();
              if (/done|completed|live|production/.test(st)) devMap[name].done += 1;
              else if (/progress|dev|qa|review/.test(st)) devMap[name].inProg += 1;
            });
            const arr = Object.entries(devMap).map(([n, s]) => {
              const completionRate = s.tickets > 0 ? Math.round((s.done / s.tickets) * 100) : 0;
              const score = Math.min(100, Math.max(0, Math.round(completionRate * 0.5 + (s.inProg > 0 ? 20 : s.done > 0 ? 20 : 0) + Math.max(0, 30 - s.bugs * 10))));
              return { name: n, ...s, completionRate, score };
            }).sort((a, b) => b.score - a.score);
            results[sp.id] = arr;
          }
        } catch { /* ignore */ }
      }));
      setSprintDevData(results);
    };
    fetchDevForSprints();
  }, [kpiData]);

  // ══════════ HANDLERS ══════════

  const handleSprintClick = (sprint) => {
    setSelectedSprint(sprint);
    setSelectedTicket(null);
    setTicketSearch("");
    setExpandedParents({});
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setDetailTab('Details');
    setParentTicketStack([]);
  };

  const handleChildTicketClick = (childTicket, returnTab) => {
    setParentTicketStack(prev => [...prev, { ticket: selectedTicket, tab: returnTab }]);
    setSelectedTicket(childTicket);
    setDetailTab('Details');
  };

  const handleBackToCards = () => {
    setSelectedSprint(null);
    setSelectedTicket(null);
    setTicketList([]);
    setParentTicketStack([]);
  };

  const handleBackToTickets = () => {
    if (parentTicketStack.length > 0) {
      const stack = [...parentTicketStack];
      const parent = stack.pop();
      setParentTicketStack(stack);
      setSelectedTicket(parent.ticket);
      setDetailTab(parent.tab);
    } else {
      setSelectedTicket(null);
    }
  };

  const toggleParentExpand = (parentId) => {
    setExpandedParents(prev => ({ ...prev, [parentId]: !prev[parentId] }));
  };

  // Inline edit
  const handleInlineEdit = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/`, {
        method: "PUT", headers,
        body: JSON.stringify({ id: editRowId, sprint_id: selectedSprint.id, ...editedValues, company_id: 1 }),
      });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast("Ticket updated"); setEditRowId(null); setRefreshTrigger(p => !p); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Update failed"); }
  };

  // Side panel edit
  const handleSidePanelEdit = (ticket) => { setEditData(ticket); setIsSidebarOpen(true); };
  const handleSidePanelClose = () => { setIsSidebarOpen(false); setEditData(null); setRefreshTrigger(p => !p); };

  // Delete
  const [deleteConfirm, setDeleteConfirm] = useState(null); // ticket id to confirm

  const handleDeleteTicket = (ticketId) => {
    setDeleteConfirm(ticketId);
  };

  const confirmDeleteTicket = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/?id=${deleteConfirm}`, { method: "DELETE", headers });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast("Ticket deleted");
        setRefreshTrigger(p => !p);
        if (selectedTicket?.id === deleteConfirm) setSelectedTicket(null);
        // If the last bug of the open story was deleted while on the Bugs tab,
        // fall back to Details (the Bugs tab disappears when the list is empty).
        const bugs = selectedTicket?.bug_tickets || [];
        if (detailTab === 'Bugs' && bugs.length <= 1 && bugs.some(b => b.id === deleteConfirm)) setDetailTab('Details');
      }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Delete failed"); }
    finally { setDeleteConfirm(null); }
  };

  // Sprint CRUD
  const handleSaveSprint = async () => {
    try {
      const payload = { ...sprintForm, project_id: currentProject, company_id: 1, status: sprintForm.sprint_status || "ONGOING" };
      if (editSprintId) payload.id = editSprintId;
      const res = await fetch(`${API_BASE_URL}ticketing/sprint/`, { method: editSprintId ? "PUT" : "POST", headers, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast(editSprintId ? "Sprint updated" : "Sprint created"); setShowSprintPopup(false); setEditSprintId(null); setSprintForm({ sprint_name: "", short_name: "", start_date: "", end_date: "", team_name: "", team_members: [], sprint_status: "ONGOING", is_live_support: false }); setRefreshTrigger(p => !p); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Save failed"); }
  };

  const handleEditSprint = (sprint) => {
    const validStatuses = ["ONGOING", "COMPLETED", "OVERDUE", "CANCELLED"];
    const safeStatus = validStatuses.includes(sprint.sprint_status) ? sprint.sprint_status : "ONGOING";
    setSprintForm({ sprint_name: sprint.sprint_name, short_name: sprint.short_name || "", start_date: sprint.start_date || "", end_date: sprint.end_date || "", team_name: sprint.team_name || "", team_members: sprint.team_members || [], sprint_status: safeStatus, is_live_support: !!sprint.is_live_support });
    setEditSprintId(sprint.id);
    setShowSprintPopup(true);
  };

  // Move to sprint
  const handleMoveTicket = async () => {
    if (!menuTicket || !moveTarget) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/move-ticket/`, { method: "POST", headers, body: JSON.stringify({ ticket_id: menuTicket.id, new_sprint_id: moveTarget }) });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast("Ticket moved"); setMovePopup(false); setMenuTicket(null); setRefreshTrigger(p => !p); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Move failed"); }
  };

  // Raise a move-to-live request for a ticket. Backend enforces the rules
  // (live-support sprint, QA/UAT-pass status, permission, no duplicate request).
  const handleSendToLive = async (ticket) => {
    if (!ticket) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/live-move/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ticket_id: ticket.id }),
      });
      const data = await res.json();
      if (data.status === "success") showSuccessToast("Live move request sent");
      else showErrorToast(data.message || "Could not send request");
    } catch (e) {
      showErrorToast("Could not send request");
    } finally {
      closeContextMenu();
    }
  };

  // Add child story or bug
  const openAddTicketPopup = (parentTicket, type) => {
    setAddTicketParent(parentTicket);
    setAddTicketType(type);
    setAddTicketForm({ short_description: "", status_id: ticketStatuses?.[0]?.id || "", priority_id: "", bug_type: "Development", due_date: "" });
    setAddTicketPopup(true);
    closeContextMenu();
  };

  const handleSaveNewTicket = async () => {
    if (!selectedSprint || !addTicketForm.short_description.trim()) {
      showErrorToast("Description is required");
      return;
    }
    try {
      const payload = {
        short_description: addTicketForm.short_description,
        status_id: addTicketForm.status_id || (ticketStatuses?.[0]?.id),
        sprint_id: selectedSprint.id,
        parent_ticket_id: addTicketParent ? addTicketParent.id : null,
        story_type: addTicketParent ? "Child" : "Parent",
        ticket_type: addTicketType,
        priority_id: addTicketForm.priority_id || null,
        due_date: addTicketForm.due_date || null,
        company_id: 1,
      };
      if (addTicketType === "Bug") {
        payload.bug_type = addTicketForm.bug_type || "Development";
      }
      const res = await fetch(`${API_BASE_URL}ticketing/ticket/`, { method: "POST", headers, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(`${addTicketType} created`);
        setAddTicketPopup(false);
        setRefreshTrigger(p => !p);
      } else showErrorToast(data.message);
    } catch (e) { showErrorToast("Failed to create ticket"); }
  };

  // Comment
  const handleAddComment = async (text, parentCommentId = null) => {
    if (!selectedTicket || !text.trim()) return;
    try {
      const payload = { ticket_id: selectedTicket.id, comments: text, is_parent_comment: !parentCommentId };
      if (parentCommentId) payload.parent_comment_id = parentCommentId;
      const res = await fetch(`${API_BASE_URL}ticketing/comments/`, { method: "POST", headers, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.status === "success") { fetchComments(selectedTicket.id); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Failed to add comment"); }
  };

  // Download attachment
  const handleDownload = async (att) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/attachements/?id=${att.id}`, { headers });
      const data = await res.json();
      if (data.status === "success" && data.data) window.open(data.data, "_blank");
    } catch (e) { showErrorToast("Download failed"); }
  };

  // Upload attachments
  const handleUploadAttachments = async () => {
    if (!pendingFiles.length || !selectedTicket) return;
    setUploadingFiles(true);
    try {
      const formData = new FormData();
      formData.append("ticket_id", selectedTicket.id);
      pendingFiles.forEach((file) => formData.append("attachments", file));
      const res = await fetch(`${API_BASE_URL}ticketing/attachements/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        body: formData,
      });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(data.message);
        setPendingFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        fetchAttachments(selectedTicket.id);
      } else showErrorToast(data.message || "Upload failed");
    } catch (e) { showErrorToast("Upload failed"); }
    finally { setUploadingFiles(false); }
  };

  // Delete attachment
  const handleDeleteAttachment = async (att) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/attachements/?id=${att.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(data.message);
        fetchAttachments(selectedTicket.id);
      } else showErrorToast("Failed to delete attachment");
    } catch (e) { showErrorToast("Failed to delete attachment"); }
  };

  // Add link to ticket
  const handleAddLink = async () => {
    if (!linkUrl || !linkName || !selectedTicket) return;
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/links/`, {
        method: "POST", headers,
        body: JSON.stringify({ ticket_id: selectedTicket.id, url: linkUrl, url_name: linkName }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(data.message);
        setLinkUrl(""); setLinkName(""); setShowAddLink(false);
        // Refresh ticket to get updated links
        const tRes = await fetch(`${API_BASE_URL}ticketing/ticket/?sprint_id=${selectedTicket.sprint_id}&id=${selectedTicket.id}`, { headers });
        const tData = await tRes.json();
        if (tData.status === "success") setSelectedTicket(tData.data);
      } else showErrorToast(data.message || "Failed to add link");
    } catch (e) { showErrorToast("Failed to add link"); }
  };

  // Delete link
  const handleDeleteLink = async (linkItem) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/links/?ticket_link_id=${linkItem.id}`, {
        method: "DELETE", headers,
      });
      const data = await res.json();
      if (data.status === "success") {
        showSuccessToast(data.message);
        const tRes = await fetch(`${API_BASE_URL}ticketing/ticket/?sprint_id=${selectedTicket.sprint_id}&id=${selectedTicket.id}`, { headers });
        const tData = await tRes.json();
        if (tData.status === "success") setSelectedTicket(tData.data);
      } else showErrorToast("Failed to delete link");
    } catch (e) { showErrorToast("Failed to delete link"); }
  };

  // Add branch to ticket
  const handleAddBranch = async () => {
    if (!selectedTicket || !addBranchForm.repository_id || !addBranchForm.branch_name.trim()) {
      showErrorToast("Repository and branch name are required");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/gitlab/branches/`, {
        method: "POST", headers,
        body: JSON.stringify({ ticket_id: selectedTicket.id, ...addBranchForm }),
      });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast("Branch added"); setShowAddBranch(false); setAddBranchForm({ repository_id: '', branch_name: '', developer_id: '', branch_type: 'feature' }); fetchBranches(selectedTicket.id); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Failed to add branch"); }
  };

  // Sync branch from GitLab
  const handleSyncBranch = async (branchId) => {
    setBranchSyncing(branchId);
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/gitlab/branches/sync/`, {
        method: "POST", headers,
        body: JSON.stringify({ id: branchId }),
      });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast("Branch synced"); fetchBranches(selectedTicket.id); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Sync failed"); }
    finally { setBranchSyncing(null); }
  };

  // Delete branch mapping
  const handleDeleteBranch = async (branchId) => {
    try {
      const res = await fetch(`${API_BASE_URL}ticketing/gitlab/branches/?id=${branchId}`, { method: "DELETE", headers });
      const data = await res.json();
      if (data.status === "success") { showSuccessToast("Branch removed"); fetchBranches(selectedTicket.id); }
      else showErrorToast(data.message);
    } catch (e) { showErrorToast("Failed to remove branch"); }
  };

  // Context menu
  const openContextMenu = (e, ticket) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuTicket(ticket);

    // Diagnose why the "Send to Live" (Request Live Move) action shows or not.
    const hasFeature = Boolean(liveSupportEnabled);
    const sprintIsLive = Boolean(selectedSprint?.is_live_support);
    const hasPermission = Boolean(userPermission?.includes('Request Live Move'));
    if (hasFeature && sprintIsLive && hasPermission) {
      console.log("[Request Live Move] visible — all conditions met");
    } else {
      const reasons = [];
      if (!hasFeature) reasons.push("company feature 'liveSupportEnabled' is OFF (localStorage.liveSupportEnabled)");
      if (!sprintIsLive) reasons.push("selected sprint is NOT a live-support sprint (selectedSprint.is_live_support is falsy)");
      if (!hasPermission) reasons.push("user is missing the 'Request Live Move' permission (userPermissions)");
      console.log("[Request Live Move] hidden because:", reasons, {
        liveSupportEnabled: hasFeature,
        "selectedSprint.is_live_support": sprintIsLive,
        userPermission,
        hasRequestLiveMovePermission: hasPermission,
      });
    }
  };
  const closeContextMenu = () => { setMenuAnchorEl(null); };

  // ══════════ COMPUTED ══════════

  const filteredTickets = useMemo(() => {
    let result = ticketList;
    if (ticketSearch.trim()) {
      const q = ticketSearch.toLowerCase();
      result = result.filter(t =>
        (t.parent_code || '').toLowerCase().includes(q) ||
        (t.short_description || '').toLowerCase().includes(q) ||
        (t.assignee_name || '').toLowerCase().includes(q)
      );
    }
    if (filterStatus) {
      result = result.filter(t => (t.ticket_status || '').toLowerCase() === filterStatus.toLowerCase());
    }
    if (filterPriority) {
      result = result.filter(t => (t.priority_name || '').toLowerCase() === filterPriority.toLowerCase());
    }
    if (filterAssigned) {
      result = result.filter(t => {
        const devs = Array.isArray(t.developers) ? t.developers : [];
        return devs.some(d => String(d.id || d) === String(filterAssigned)) || String(t.assignee_id) === String(filterAssigned);
      });
    }
    if (filterType) {
      result = result.filter(t => (t.ticket_type || '').toLowerCase() === filterType.toLowerCase());
    }
    if (dueSort) {
      // Tickets with no due date always sink to the bottom, in both directions —
      // an absent date is not "earliest", it is simply unset.
      result = [...result].sort((a, b) => {
        const av = a.due_date ? new Date(a.due_date).getTime() : null;
        const bv = b.due_date ? new Date(b.due_date).getTime() : null;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return dueSort === 'asc' ? av - bv : bv - av;
      });
    }
    return result;
  }, [ticketList, ticketSearch, filterStatus, filterPriority, filterAssigned, filterType, dueSort]);

  // All flat ticket codes for switcher (parents + children + bugs)
  const allTicketCodes = useMemo(() => {
    const codes = [];
    ticketList.forEach(t => {
      codes.push({ id: t.id, code: t.parent_code, type: t.ticket_type, ticket: t });
      if (t.child_tickets) {
        t.child_tickets.forEach(child => {
          codes.push({ id: child.id, code: child.child_code, type: child.ticket_type, ticket: child });
          if (child.bug_tickets) {
            child.bug_tickets.forEach(bug => {
              codes.push({ id: bug.id, code: bug.bug_code, type: 'Bug', ticket: bug });
            });
          }
        });
      }
      if (t.bug_tickets) {
        t.bug_tickets.forEach(bug => {
          codes.push({ id: bug.id, code: bug.bug_code, type: 'Bug', ticket: bug });
        });
      }
    });
    return codes;
  }, [ticketList]);

  // ══════════ RENDER HELPERS ══════════

  const categorizeStatus = (statusBreakdown) => {
    const completed = Object.entries(statusBreakdown).filter(([k]) => k.toLowerCase().includes('done') || k.toLowerCase().includes('completed') || k.toLowerCase().includes('closed')).reduce((s, [, v]) => s + v, 0);
    const inProgress = Object.entries(statusBreakdown).filter(([k]) => k.toLowerCase().includes('progress') || k.toLowerCase().includes('review') || k.toLowerCase().includes('qa') || k.toLowerCase().includes('dev')).reduce((s, [, v]) => s + v, 0);
    const todo = Object.entries(statusBreakdown).filter(([k]) => k.toLowerCase().includes('to do') || k.toLowerCase().includes('todo') || k.toLowerCase().includes('open') || k.toLowerCase().includes('new')).reduce((s, [, v]) => s + v, 0);
    const other = Object.values(statusBreakdown).reduce((s, v) => s + v, 0) - completed - inProgress - todo;
    return { completed, inProgress, todo, other };
  };

  // ══════════ RENDER ══════════

  return (
    <div className="sprint-dash">
      {/* ── Project selector + Add Sprint (hidden on Level 2/3) ── */}
      {!selectedSprint && (
        <div className="fd-selector-row" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
          <FilterDropdown label="Project" value={currentProject} options={projects} onChange={v => { setCurrentProject(v); setSelectedSprint(null); setSelectedTicket(null); }} renderLabel={o => o?.project_name || ''} getValue={o => String(o?.id)} />
          <div style={{ marginLeft: 'auto' }}>
            <button className="fd-btn-primary" onClick={() => { setEditSprintId(null); setSprintForm({ sprint_name: "", short_name: "", start_date: "", end_date: "", team_name: "", team_members: [], sprint_status: "ONGOING", is_live_support: false }); setShowSprintPopup(true); }}><FiPlus size={13} /> Add Sprint</button>
          </div>
        </div>
      )}

      {/* ── Sprint status tabs ── */}
      {!selectedSprint && (
        <div className="sprint-status-tabs" style={{ marginBottom: 6 }}>
          {SPRINT_STATUS_OPTIONS.map(opt => (
            <button key={opt.value} className={`sprint-status-tab ${sprintStatusFilter === opt.value ? 'sprint-status-tab--active' : ''}`} onClick={() => setSprintStatusFilter(opt.value)}>
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* ═══ LEVEL 1: KPI Cards ═══ */}
      {!selectedSprint && (
        kpiLoading ? (
          <div className="sprint-empty-state"><CircularProgress size={36} sx={{ color: 'var(--accent)' }} /></div>
        ) : kpiData.length === 0 ? (
          <div className="sprint-empty-state">No sprints found for this project and status filter.</div>
        ) : (
          <div className="sprint-kpi-grid">
            {kpiData.map(sprint => {
              const cat = categorizeStatus(sprint.status_breakdown);
              const total = sprint.total_tickets;
              const segments = [
                { count: cat.completed, color: "green" },
                { count: cat.inProgress, color: "blue" },
                { count: cat.todo, color: "yellow" },
                { count: cat.other, color: "gray" },
              ];
              return (
                <div key={sprint.id} className={`sprint-kpi-card`} onClick={() => handleSprintClick(sprint)}>
                  <div className="sprint-kpi-left">
                    <DonutChart segments={segments} total={total} />
                  </div>
                  <div className="sprint-kpi-right">
                    <div className="sprint-kpi-header">
                      <div>
                        <h3 className="sprint-kpi-name">{sprint.sprint_name} <span className="sprint-kpi-code">({sprint.short_name})</span></h3>
                      </div>
                      <span className={`sprint-status-pill ${STATUS_PILL_CLASS[sprint.sprint_status] || ''}`}>{sprint.sprint_status}</span>
                    </div>
                    <div className="sprint-kpi-meta">
                      <span>{formatDate(sprint.start_date)} – {formatDate(sprint.end_date)}</span>
                      {sprint.team_name && <span>Team: {sprint.team_name}</span>}
                    </div>
                    <div className="sprint-kpi-bars">
                      <KPIBar label="Done" count={cat.completed} total={total} color="green" />
                      <KPIBar label="In Progress" count={cat.inProgress} total={total} color="blue" />
                      <KPIBar label="To Do" count={cat.todo} total={total} color="yellow" />
                    </div>
                    {sprint.bug_count > 0 && (
                      <div className="sprint-kpi-bugs"><GoBug size={12} /> {sprint.bug_count} bug{sprint.bug_count !== 1 ? 's' : ''}</div>
                    )}
                  </div>
                  {/* Edit sprint button */}
                  <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8, color: 'var(--text-subtle)' }} onClick={(e) => { e.stopPropagation(); handleEditSprint(sprint); }}>
                    <FiEdit2 size={13} />
                  </IconButton>
                  {/* Developer performance strip */}
                  {sprintDevData[sprint.id] && sprintDevData[sprint.id].length > 0 && (
                    <div className="sprint-kpi-devs" onClick={e => e.stopPropagation()}>
                      {sprintDevData[sprint.id].slice(0, 5).map((dev, di) => {
                        const avatarColors = ['#7c5cfc','#1976d2','#e53935','#43a047','#f5a623','#8e24aa','#00897b','#00acc1'];
                        const bg = avatarColors[(dev.name || '?').charCodeAt(0) % avatarColors.length];
                        const scoreColor = dev.score >= 70 ? '#4caf50' : dev.score >= 45 ? '#ff9800' : '#f44336';
                        return (
                          <div key={dev.name} className="sprint-kpi-dev">
                            <span className="sprint-kpi-dev__avatar" style={{ background: bg }}>{(dev.name || '?')[0].toUpperCase()}</span>
                            <div className="sprint-kpi-dev__info">
                              <span className="sprint-kpi-dev__name">{dev.name}</span>
                              <span className="sprint-kpi-dev__stat">{dev.done}/{dev.tickets} done{dev.inProg > 0 ? ` · ${dev.inProg} active` : ''}</span>
                            </div>
                            <div className="sprint-kpi-dev__bar"><div style={{ width: `${dev.completionRate}%`, background: scoreColor }} /></div>
                            <div className="sprint-kpi-dev__score" style={{ color: scoreColor, borderColor: scoreColor }}>{dev.score}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ═══ LEVEL 2: Sprint Ticket Dashboard ═══ */}
      {selectedSprint && !selectedTicket && (() => {
        // Status-wise breakdown across Story + Epic only (parents + non-Bug children).
        // Bugs are counted separately via the Bugs KPI, so excluding them here keeps the
        // Done / In Progress / To Do counts consistent with Total Tickets.
        const statusMap = {};
        const walk = (list) => {
          list.forEach(t => {
            if (t.ticket_type === 'Bug') return;
            const s = t.status_name || t.ticket_status || 'Unknown';
            statusMap[s] = (statusMap[s] || 0) + 1;
            if (t.child_tickets) walk(t.child_tickets);
          });
        };
        walk(ticketList);
        const todoStatuses = /^(to\s*do|hold)$/i;
        const doneStatuses = /^(production|live|moved\s*to\s*live|moved\s*to\s*production|not\s*a\s*bug)$/i;
        const todoCount = Object.entries(statusMap).filter(([k]) => todoStatuses.test(k.trim())).reduce((s,[,v]) => s+v, 0);
        const doneCount = Object.entries(statusMap).filter(([k]) => doneStatuses.test(k.trim())).reduce((s,[,v]) => s+v, 0);
        const inProgCount = Object.entries(statusMap).filter(([k]) => !todoStatuses.test(k.trim()) && !doneStatuses.test(k.trim())).reduce((s,[,v]) => s+v, 0);
        const completionPct = ticketCount > 0 ? Math.round((doneCount / ticketCount) * 100) : 0;

        // Use master values for filter dropdowns (not just what's in current tickets)
        const masterStatuses = ticketStatuses.filter(s => s.is_active).map(s => s.ticket_status);
        const masterPriorities = priorityTypes.filter(p => p.is_active).map(p => p.priority);
        const masterEmployees = employees.filter(e => e.is_active).map(e => ({ id: e.id, name: e.name }));
        const hasActiveFilters = filterStatus || filterPriority || filterAssigned;

        // Custom filter dropdown component
        const FilterDrop = ({ label, value, options, onChange, renderLabel }) => {
          const [open, setOpen] = React.useState(false);
          const [search, setSearch] = React.useState('');
          const ref = React.useRef(null);

          React.useEffect(() => {
            const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
            document.addEventListener('mousedown', handler);
            return () => document.removeEventListener('mousedown', handler);
          }, []);

          const filtered = search ? options.filter(o => {
            const lbl = renderLabel ? renderLabel(o) : String(o);
            return lbl.toLowerCase().includes(search.toLowerCase());
          }) : options;

          const selectedLabel = value ? (renderLabel ? renderLabel(options.find(o => (typeof o === 'object' ? String(o.id) : o) === String(value))) : value) : null;

          return (
            <div className="sl2-fdrop" ref={ref}>
              <button className={`sl2-fdrop__trigger ${value ? 'sl2-fdrop__trigger--active' : ''}`} onClick={() => setOpen(!open)}>
                <span>{selectedLabel || label}</span>
                <FiChevronDown size={12} className={`sl2-fdrop__chevron ${open ? 'sl2-fdrop__chevron--open' : ''}`} />
              </button>
              {open && (
                <div className="sl2-fdrop__panel">
                  <div className="sl2-fdrop__search">
                    <FiSearch size={12} />
                    <input type="text" placeholder={`Search ${label.toLowerCase()}...`} value={search} onChange={e => setSearch(e.target.value)} autoFocus />
                  </div>
                  <div className="sl2-fdrop__list">
                    <div className={`sl2-fdrop__item ${!value ? 'sl2-fdrop__item--selected' : ''}`} onClick={() => { onChange(''); setOpen(false); setSearch(''); }}>
                      All {label}
                    </div>
                    {filtered.map(o => {
                      const val = typeof o === 'object' ? String(o.id) : o;
                      const lbl = renderLabel ? renderLabel(o) : String(o);
                      const isSelected = String(value) === val;
                      return (
                        <div key={val} className={`sl2-fdrop__item ${isSelected ? 'sl2-fdrop__item--selected' : ''}`} onClick={() => { onChange(val); setOpen(false); setSearch(''); }}>
                          {lbl}
                          {isSelected && <span className="sl2-fdrop__check">✓</span>}
                        </div>
                      );
                    })}
                    {filtered.length === 0 && <div className="sl2-fdrop__empty">No results</div>}
                  </div>
                </div>
              )}
            </div>
          );
        };

        // Developer avatar helper
        const DevAvatar = ({ name }) => {
          const colors = ['#7c5cfc','#1976d2','#e53935','#43a047','#f5a623','#8e24aa','#00897b'];
          const initial = (name || '?')[0].toUpperCase();
          const color = colors[initial.charCodeAt(0) % colors.length];
          return <span title={name} style={{ width: 28, height: 28, borderRadius: '50%', background: color, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 700, flexShrink: 0, marginLeft: -6, border: '2px solid var(--bg-secondary)' }}>{initial}</span>;
        };

        // Priority pill
        const PrioPill = ({ name }) => {
          const n = (name || '').toLowerCase();
          const color = n.includes('high') ? 'var(--status-red)' : n.includes('low') ? 'var(--status-green)' : '#f5a623';
          return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.76rem', color }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />{name || '-'}</span>;
        };

        // Status pill with auto color
        const StatusPill = ({ name }) => {
          const n = (name || '').toLowerCase();
          let cls = 'fd-status-pill--muted';
          if (/done|completed|live|production/i.test(n)) cls = 'fd-status-pill--success';
          else if (/progress|dev|qa|review/i.test(n)) cls = 'fd-status-pill--info';
          else if (/fail|reject|block/i.test(n)) cls = 'fd-status-pill--danger';
          return <span className={`fd-status-pill ${cls}`}>{name || '-'}</span>;
        };

        return (
        <div className="sl2-page">
          {/* ── Header: Title + Create button ── */}
          <div className="sl2-header">
            <div className="sl2-header__left">
              <button className="sl2-back-btn" onClick={handleBackToCards}>←</button>
              <h1 className="sl2-title">{selectedSprint.sprint_name}</h1>
              <span className={`sprint-status-pill ${STATUS_PILL_CLASS[selectedSprint.sprint_status] || ''}`}>{selectedSprint.sprint_status}</span>
            </div>
            <div className="sl2-header__right">
              <button className="sl2-edit-sprint-btn" onClick={() => handleEditSprint(selectedSprint)}><FiEdit2 size={13} /></button>
            </div>
          </div>

          {/* ── KPI Cards ── */}
          <div className="sl2-kpi-row">
            <div className="sl2-kpi">
              <div className="sl2-kpi__ring">
                <svg viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="var(--border-subtle)" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={`${completionPct * 0.974} ${97.4 - completionPct * 0.974}`} transform="rotate(-90 18 18)" />
                </svg>
                <span className="sl2-kpi__ring-val">{completionPct}%</span>
              </div>
              <div className="sl2-kpi__info">
                <span className="sl2-kpi__val">{ticketCount}</span>
                <span className="sl2-kpi__label">Total Tickets</span>
              </div>
            </div>
            <div className="sl2-kpi sl2-kpi--green">
              <span className="sl2-kpi__val">{doneCount}</span>
              <span className="sl2-kpi__label">Done</span>
              <span className="sl2-kpi__bar"><span style={{ width: `${ticketCount ? (doneCount / ticketCount) * 100 : 0}%` }} /></span>
            </div>
            <div className="sl2-kpi sl2-kpi--blue">
              <span className="sl2-kpi__val">{inProgCount}</span>
              <span className="sl2-kpi__label">In Progress</span>
              <span className="sl2-kpi__bar"><span style={{ width: `${ticketCount ? (inProgCount / ticketCount) * 100 : 0}%` }} /></span>
            </div>
            <div className="sl2-kpi sl2-kpi--orange">
              <span className="sl2-kpi__val">{todoCount}</span>
              <span className="sl2-kpi__label">To Do</span>
              <span className="sl2-kpi__bar"><span style={{ width: `${ticketCount ? (todoCount / ticketCount) * 100 : 0}%` }} /></span>
            </div>
            <div className="sl2-kpi sl2-kpi--red">
              <span className="sl2-kpi__val">{bugCount}</span>
              <span className="sl2-kpi__label">Bugs</span>
              <span className="sl2-kpi__bar"><span style={{ width: `${(ticketCount + bugCount) ? (bugCount / (ticketCount + bugCount)) * 100 : 0}%` }} /></span>
            </div>
          </div>

          {/* ── Toolbar: Count + Filters + Search ── */}
          <div className="sl2-toolbar">
            <span className="sl2-toolbar__count">{filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}</span>
            <div className="sl2-toolbar__filters">
              <FilterDrop label="Status" value={filterStatus} options={masterStatuses} onChange={setFilterStatus} />
              <FilterDrop label="Priority" value={filterPriority} options={masterPriorities} onChange={setFilterPriority} />
              <FilterDrop label="Assigned" value={filterAssigned} options={masterEmployees} onChange={(v) => setFilterAssigned(v)} renderLabel={(o) => o?.name || ''} />
              {hasActiveFilters && (
                <button className="sl2-filter-pill sl2-filter-pill--clear" onClick={() => { setFilterStatus(''); setFilterPriority(''); setFilterAssigned(''); }}>
                  <FiX size={11} /> Clear
                </button>
              )}
            </div>
            <div className="sl2-toolbar__search">
              <input type="text" placeholder="Search..." value={ticketSearch} onChange={(e) => setTicketSearch(e.target.value)} />
              <FiSearch size={14} />
            </div>
            {liveSupportEnabled && selectedSprint?.is_live_support && (
              <button className="fd-btn-primary" onClick={() => openAddTicketPopup(null, 'Story')}>
                <FiPlus size={13} /> Add Ticket
              </button>
            )}
          </div>

          {/* ── Table ── */}
          <div className="sl2-table-card">
            {ticketLoading ? (
              <div className="sprint-empty-state"><CircularProgress size={32} sx={{ color: 'var(--accent)' }} /></div>
            ) : (
              <table className="sl2-table">
                <thead>
                  <tr>
                    <th className="sl2-th--ticket">Ticket No</th>
                    <th className="sl2-th--desc">Description</th>
                    <th className="sl2-th--status">Status</th>
                    <th className="sl2-th--prio">Priority</th>
                    <th
                      className="sl2-th--due sl2-th--sortable"
                      onClick={() => setDueSort(p => (p === 'asc' ? 'desc' : p === 'desc' ? '' : 'asc'))}
                      title="Sort by due date"
                    >
                      Due Date{dueSort === 'asc' ? ' ▲' : dueSort === 'desc' ? ' ▼' : ''}
                    </th>
                    <th className="sl2-th--assigned">Assigned</th>
                    <th className="sl2-th--qa">QA Assigned</th>
                    <th className="sl2-th--overview">Overview</th>
                    <th className="sl2-th--action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.length > 0 ? filteredTickets.map((ticket, idx) => {
                    const hasChildren = ticket.child_tickets?.length > 0;
                    const hasBugs = ticket.bug_tickets?.length > 0;
                    const isExp = expandedParents[ticket.id];

                    const renderRow = (t, indent, key) => {
                      const tCode = t.parent_code || t.child_code || t.bug_code;
                      const tDevArr = Array.isArray(t.developers) ? t.developers : [];
                      const tType = t.ticket_type || (t.bug_code ? 'Bug' : 'Story');
                      return (
                        <tr key={key || t.id} className="sl2-row" onClick={() => handleTicketClick(t)}>
                          <td style={{ paddingLeft: indent }}>
                            <span className="sl2-ticket-code">{tCode}</span>
                          </td>
                          <td>
                            <span className="sl2-ticket-desc">{t.short_description || '-'}</span>
                          </td>
                          <td className="sl2-td--status" onClick={e => openStatusDrop(e, t.id)}>
                            <StatusPill name={t.status_name || t.ticket_status} />
                          </td>
                          <td><PrioPill name={t.priority_name} /></td>
                          <td><DueDateCell value={t.due_date} /></td>
                          <td>
                            <div className="sl2-assigned-cell">
                              {tDevArr.slice(0, 3).map((d, i) => <DevAvatar key={d.id || i} name={d.name || d} />)}
                              {tDevArr.length > 3 && <span className="sl2-dev-overflow">+{tDevArr.length - 3}</span>}
                              {tDevArr.length > 0 && <span className="sl2-dev-name">{tDevArr[0]?.name || tDevArr[0]}</span>}
                              {tDevArr.length === 0 && <span className="sl2-no-assign">-</span>}
                            </div>
                          </td>
                          <td>
                            <div className="sl2-assigned-cell">
                              {t.qa_name ? (
                                <>
                                  <DevAvatar name={t.qa_name} />
                                  <span className="sl2-dev-name">{t.qa_name}</span>
                                </>
                              ) : <span className="sl2-no-assign">-</span>}
                            </div>
                          </td>
                          <td className="sl2-overview-cell">
                            {(t.bug_count > 0 || t.child_count > 0 || t.child_tickets?.length > 0) ? (
                              <div className="sl2-overview-chips">
                                {(t.child_count > 0 || t.child_tickets?.length > 0) && <span className="sl2-child-chip"><FiGitBranch size={10} /> {t.child_count || t.child_tickets?.length}</span>}
                                {t.bug_count > 0 && <span className="sl2-bug-chip"><GoBug size={10} /> {t.bug_count}</span>}
                              </div>
                            ) : '-'}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="sl2-action-cell">
                              {(tType === 'Epic' || tType === 'Story') ? (
                                <IconButton size="small" onClick={(e) => openContextMenu(e, t)} title="Actions">
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                <>
                                  {userPermission?.includes('Delete Ticket') && <button className="fd-action-btn fd-action-btn--danger" onClick={() => handleDeleteTicket(t.id)} title="Delete"><FiTrash2 size={12} /></button>}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    };

                    return (
                      <React.Fragment key={ticket.id}>
                        {renderRow(ticket, 14, ticket.id)}
                        {isExp && ticket.child_tickets?.map(child => (
                          <React.Fragment key={child.id}>
                            {renderRow(child, 34, child.id)}
                            {child.bug_tickets?.map(bug => renderRow(bug, 54, bug.id))}
                          </React.Fragment>
                        ))}
                        {isExp && ticket.bug_tickets?.map(bug => renderRow(bug, 34, bug.id))}
                      </React.Fragment>
                    );
                  }) : (
                    <tr><td colSpan={9} className="sl2-empty">{ticketSearch || hasActiveFilters ? 'No tickets match your filters' : 'No tickets in this sprint'}</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
        );
      })()}

      {/* ═══ Inline Status Dropdown (fixed position) ═══ */}
      {editingStatusId && (() => {
        // Find the ticket being edited across all levels
        let editTicket = null;
        for (const t of ticketList) {
          if (t.id === editingStatusId) { editTicket = t; break; }
          if (t.child_tickets) for (const c of t.child_tickets) {
            if (c.id === editingStatusId) { editTicket = c; break; }
            if (c.bug_tickets) for (const b of c.bug_tickets) { if (b.id === editingStatusId) { editTicket = b; break; } }
          }
          if (t.bug_tickets) for (const b of t.bug_tickets) { if (b.id === editingStatusId) { editTicket = b; break; } }
          if (editTicket) break;
        }
        if (!editTicket) return null;
        return (
          <div className="sl2-status-edit" style={{ top: statusDropPos.top, left: statusDropPos.left }} onClick={e => e.stopPropagation()}>
            {ticketStatuses.filter(s => s.is_active).map(s => (
              <div key={s.id} className={`sl2-status-option ${String(editTicket.status_id || editTicket.status) === String(s.id) ? 'sl2-status-option--current' : ''}`} onClick={() => handleInlineStatusChange(editTicket, s.id)}>
                {s.ticket_status}
              </div>
            ))}
          </div>
        );
      })()}

      {/* ═══ Delete Confirmation ═══ */}
      {deleteConfirm && (
        <div className="sl2-qc-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="sl2-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="sl2-confirm-icon"><FiTrash2 size={22} /></div>
            <h3>Delete Ticket</h3>
            <p>Are you sure you want to delete this ticket? This action cannot be undone.</p>
            <div className="sl2-confirm-actions">
              <button className="sl2-qc-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="sl2-confirm-delete" onClick={confirmDeleteTicket}><FiTrash2 size={13} /> Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Move Back to Client Requirements ═══ */}
      {moveBackTicket && (
        <div className="sl2-qc-overlay" onClick={() => setMoveBackTicket(null)}>
          <div className="sl2-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="sl2-confirm-icon"><FiRotateCcw size={22} /></div>
            <h3>Move Back to Client Requirements</h3>
            {moveBackTicket.client_requirement_id ? (
              <p>
                This point is mapped to a Client Requirement. It will be removed from
                the sprint, its status reset to <strong>To Do</strong>, and the
                requirement returned to the queue. If moved to a sprint again, the
                same ticket is reused — no duplicates.
              </p>
            ) : (
              <>
                <p>
                  This point was created directly in the sprint. A new Client
                  Requirement will be created with its description, user story and
                  acceptance criteria, and the point will be removed from the sprint.
                  Its history will show it originated from this sprint.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left', margin: '10px 0' }}>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Client *</label>
                  <select className="crd-sprint-select" value={moveBackClientId} onChange={e => setMoveBackClientId(e.target.value)}>
                    <option value="" disabled>Select Client</option>
                    {crClients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
                  </select>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Status (optional)</label>
                  <select className="crd-sprint-select" value={moveBackStatusId} onChange={e => setMoveBackStatusId(e.target.value)}>
                    <option value="">Default (initial status)</option>
                    {crStatuses.map(s => <option key={s.id} value={s.id}>{s.client_requirement_status}</option>)}
                  </select>
                </div>
              </>
            )}
            <div className="sl2-confirm-actions">
              <button className="sl2-qc-cancel" onClick={() => setMoveBackTicket(null)}>Cancel</button>
              <button className="sl2-qc-submit" onClick={handleMoveBack} disabled={moveBackSaving || (!moveBackTicket.client_requirement_id && !moveBackClientId)}>
                {moveBackSaving ? 'Moving…' : <><FiRotateCcw size={13} /> Move Back</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ LEVEL 3: Enhanced Ticket Workspace ═══ */}
      {selectedTicket && (() => {
        const tCode = selectedTicket.parent_code || selectedTicket.child_code || selectedTicket.bug_code;
        const hasArtifact = !!selectedTicket.artifact;
        const childTickets = selectedTicket.child_tickets || [];
        const bugTickets = selectedTicket.bug_tickets || [];
        const isEpic = (selectedTicket.ticket_type || '').toLowerCase() === 'epic';
        const isStory = (selectedTicket.ticket_type || '').toLowerCase() === 'story';
        const L3_TABS = [
          'Details', 'Story & Criteria', 'Attachments', 'Branches',
          ...(isEpic && childTickets.length > 0 ? ['Stories'] : []),
          ...(isStory && bugTickets.length > 0 ? ['Bugs'] : []),
          ...(hasArtifact ? ['Normalized', 'Dev Spec', 'QA Plan'] : []),
        ];
        const [activeTab, setActiveTab] = [detailTab, setDetailTab];
        const tType = selectedTicket.ticket_type || 'Story';
        const tStatus = selectedTicket.status_name || selectedTicket.ticket_status || '-';

        return (
        <>
          {/* Top bar */}
          <div className="l3-topbar">
            <div className="l3-topbar__left">
              <button className="l3-back-btn" onClick={() => { handleBackToTickets(); setIsInlineEditing(false); setEditedTicket(null); }}>←</button>
              <span className="l3-topbar__sprint">{selectedSprint?.sprint_name}</span>
              <span className="l3-topbar__sep">/</span>
              <span className="l3-topbar__code">{tCode}</span>
            </div>
            <div className="l3-topbar__right">
              <div className="l3-switcher-wrap">
                <button className="l3-switcher-btn" title="Switch ticket"><FiChevronDown size={14} /> <span>{allTicketCodes.length} tickets</span></button>
                <div className="l3-switcher-panel">
                  <div className="l3-switcher-panel__header">All Tickets</div>
                  <div className="l3-switcher-panel__list">
                    {allTicketCodes.map(tc => (
                      <div key={tc.id} className={`l3-switcher-item ${selectedTicket.id === tc.id ? 'l3-switcher-item--active' : ''}`} onClick={() => { if (tc.ticket) { handleTicketClick(tc.ticket); setIsInlineEditing(false); setEditedTicket(null); } }}>
                        <span className="l3-switcher-item__dot" style={{ background: tc.type === 'Bug' ? 'var(--status-red)' : tc.type === 'Epic' ? 'var(--accent)' : 'var(--status-blue)' }} />
                        <span className="l3-switcher-item__code">{tc.code}</span>
                        <span className="l3-switcher-item__desc">{tc.ticket?.short_description || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {isInlineEditing ? (
                <>
                  <button className="l3-save-btn" onClick={handleInlineSave} disabled={editSaving}>{editSaving ? 'Saving...' : <><FiSave size={13} /> Save</>}</button>
                  <button className="l3-cancel-btn" onClick={() => { setIsInlineEditing(false); setEditedTicket(null); }}><FiXCircle size={13} /> Cancel</button>
                </>
              ) : (
                <>
                {(isEpic || isStory) && (selectedTicket.story_type || 'Parent') === 'Parent' && (
                  <button className="l3-cancel-btn" title="Move this point back to Client Requirements" onClick={() => openMoveBack(selectedTicket)}>
                    <FiRotateCcw size={13} /> Move to Requirements
                  </button>
                )}
                <button className="l3-edit-btn" onClick={() => {
                  const t = { ...selectedTicket };
                  // Convert JSON arrays/objects to readable text for editing
                  const toText = (val) => {
                    if (!val) return '';
                    let data = val;
                    // If it's a string that looks like JSON, try to parse it
                    if (typeof data === 'string') {
                      const s = data.trim();
                      if ((s.startsWith('[') || s.startsWith('{')) && (s.endsWith(']') || s.endsWith('}'))) {
                        try { data = JSON.parse(s.replace(/'/g, '"')); } catch { return data; }
                      } else { return data; }
                    }
                    // Now data is parsed — format it
                    if (Array.isArray(data)) {
                      return data.map(item => {
                        if (typeof item === 'string') return item;
                        if (typeof item === 'object' && item && (item.given || item.when || item.then))
                          return `Given ${item.given || ''}, When ${item.when || ''}, Then ${item.then || ''}`;
                        return typeof item === 'object' ? Object.values(item).join(' — ') : String(item);
                      }).join('\n');
                    }
                    if (typeof data === 'object' && data) {
                      if (data.given) return `Given ${data.given}, When ${data.when || ''}, Then ${data.then || ''}`;
                      return Object.values(data).join(' — ');
                    }
                    return String(data);
                  };
                  t.user_story = toText(t.user_story);
                  t.acceptance_criteria = toText(t.acceptance_criteria);
                  setIsInlineEditing(true);
                  setEditedTicket(t);
                }}><FiEdit2 size={13} /> Edit</button>
                </>
              )}
            </div>
          </div>

          {/* Main workspace */}
          <div className="l3-workspace">
            <div className="l3-detail">
              {/* Hero — View or Edit mode */}
              <div className={`l3-hero ${isInlineEditing ? 'l3-hero--editing' : ''}`}>
                {isInlineEditing ? (
                  <>
                    <input className="l3-inline-input l3-inline-input--title" value={editedTicket.short_description || ''} onChange={e => setEditedTicket(p => ({ ...p, short_description: e.target.value }))} placeholder="Ticket title" />
                    <div className="l3-edit-grid">
                      <div className="l3-edit-field">
                        <label>Type</label>
                        <select value={editedTicket.ticket_type || ''} onChange={e => setEditedTicket(p => ({ ...p, ticket_type: e.target.value }))}>
                          {ticketTypes.filter(t => t.is_active).map(t => <option key={t.id} value={t.type_name}>{t.type_name}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Status</label>
                        <select value={editedTicket.status_id || editedTicket.status || ''} onChange={e => setEditedTicket(p => ({ ...p, status_id: e.target.value }))}>
                          {ticketStatuses.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.ticket_status}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Priority</label>
                        <select value={editedTicket.priority_id || editedTicket.priority || ''} onChange={e => setEditedTicket(p => ({ ...p, priority_id: e.target.value }))}>
                          <option value="">-</option>
                          {priorityTypes.filter(p => p.is_active).map(p => <option key={p.id} value={p.id}>{p.priority}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Story Points</label>
                        <select value={editedTicket.story_points || ''} onChange={e => setEditedTicket(p => ({ ...p, story_points: e.target.value }))}>
                          <option value="">-</option>
                          {[1,2,3,5,8,13].map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Assignee</label>
                        <select value={editedTicket.assignee_id || editedTicket.assignee || ''} onChange={e => setEditedTicket(p => ({ ...p, assignee_id: e.target.value }))}>
                          <option value="">-</option>
                          {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Developers</label>
                        <Autocomplete
                          multiple size="small"
                          options={employees.filter(e => e.is_active)}
                          getOptionLabel={o => o.name || ''}
                          value={editedTicket.developers || []}
                          onChange={(_, v) => setEditedTicket(p => ({ ...p, developers: v }))}
                          isOptionEqualToValue={(o, v) => o.id === v.id}
                          renderInput={params => <TextField {...params} placeholder="Select..." variant="outlined" sx={{ '& .MuiOutlinedInput-root': { padding: '2px 6px', fontSize: '0.74rem' } }} />}
                        />
                      </div>
                      <div className="l3-edit-field">
                        <label>QA</label>
                        <select value={editedTicket.qa_id || editedTicket.qa || ''} onChange={e => setEditedTicket(p => ({ ...p, qa_id: e.target.value }))}>
                          <option value="">-</option>
                          {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Peer QA</label>
                        <select value={editedTicket.peer_qa_id || editedTicket.peer_qa || ''} onChange={e => setEditedTicket(p => ({ ...p, peer_qa_id: e.target.value }))}>
                          <option value="">-</option>
                          {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>UAT</label>
                        <select value={editedTicket.uat_id || editedTicket.uat || ''} onChange={e => setEditedTicket(p => ({ ...p, uat_id: e.target.value }))}>
                          <option value="">-</option>
                          {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                      <div className="l3-edit-field">
                        <label>Dev Est. (hrs)</label>
                        <input type="number" min="1" max="60" value={editedTicket.dev_est_time || ''} onChange={e => setEditedTicket(p => ({ ...p, dev_est_time: e.target.value }))} />
                      </div>
                      <div className="l3-edit-field">
                        <label>QA Est. (hrs)</label>
                        <input type="number" min="1" max="60" value={editedTicket.qa_est_time || ''} onChange={e => setEditedTicket(p => ({ ...p, qa_est_time: e.target.value }))} />
                      </div>
                      <div className="l3-edit-field">
                        <label>Due Date <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                        <input type="date" value={editedTicket.due_date || ''} onChange={e => setEditedTicket(p => ({ ...p, due_date: e.target.value }))} />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h1 className="l3-hero__title">{selectedTicket.short_description}</h1>
                    <div className="l3-hero__meta">
                      <span className={`sl2-type-badge sl2-type-badge--${tType.toLowerCase()}`}>{tType}</span>
                      {selectedTicket.bug_type && <span className="l3-meta-chip l3-meta-chip--red">{selectedTicket.bug_type}</span>}
                      <span className={`fd-status-pill ${/done|completed|live|production/i.test(tStatus) ? 'fd-status-pill--success' : /progress|dev|qa|review/i.test(tStatus) ? 'fd-status-pill--info' : /fail|reject|block/i.test(tStatus) ? 'fd-status-pill--danger' : 'fd-status-pill--muted'}`}>{tStatus}</span>
                      <span className="l3-meta-chip">{selectedTicket.priority_name || '-'}</span>
                      {selectedTicket.story_points && <span className="l3-meta-chip">SP: {selectedTicket.story_points}</span>}
                    </div>
                    <div className="l3-hero__details-grid">
                      <div className="l3-detail-item"><FiUser size={11} /><span className="l3-detail-item__label">Assignee</span><span>{selectedTicket.assignee_name || '-'}</span></div>
                      <div className="l3-detail-item"><FiUsers size={11} /><span className="l3-detail-item__label">Developers</span><span>{selectedTicket.developers?.length > 0 ? selectedTicket.developers.map(d => d.name || d).join(', ') : '-'}</span></div>
                      <div className="l3-detail-item"><FiUser size={11} /><span className="l3-detail-item__label">QA</span><span>{selectedTicket.qa_name || '-'}</span></div>
                      <div className="l3-detail-item"><FiUser size={11} /><span className="l3-detail-item__label">Peer QA</span><span>{selectedTicket.peer_qa_name || '-'}</span></div>
                      <div className="l3-detail-item"><FiUser size={11} /><span className="l3-detail-item__label">UAT</span><span>{selectedTicket.uat_name || '-'}</span></div>
                      <div className="l3-detail-item"><FiClock size={11} /><span className="l3-detail-item__label">Dev Est.</span><span>{selectedTicket.dev_est_time ? `${selectedTicket.dev_est_time}h` : '-'}</span></div>
                      <div className="l3-detail-item"><FiClock size={11} /><span className="l3-detail-item__label">QA Est.</span><span>{selectedTicket.qa_est_time ? `${selectedTicket.qa_est_time}h` : '-'}</span></div>
                      <div className="l3-detail-item"><FiClock size={11} /><span className="l3-detail-item__label">Due Date</span><DueDateCell value={selectedTicket.due_date} /></div>
                    </div>
                  </>
                )}
              </div>

              {/* Tabs */}
              <div className="l3-tabs">
                {L3_TABS.map(tab => (
                  <button key={tab} className={`l3-tab ${activeTab === tab ? 'l3-tab--active' : ''}`} onClick={() => setActiveTab(tab)}>
                    {tab}
                    {tab === 'Attachments' && (ticketAttachments.length + (selectedTicket?.links?.length || 0)) > 0 && <span className="l3-tab__badge">{ticketAttachments.length + (selectedTicket?.links?.length || 0)}</span>}
                    {tab === 'Branches' && ticketBranches.length > 0 && <span className="l3-tab__badge">{ticketBranches.length}</span>}
                    {tab === 'Stories' && childTickets.length > 0 && <span className="l3-tab__badge">{childTickets.length}</span>}
                    {tab === 'Bugs' && bugTickets.length > 0 && <span className="l3-tab__badge" style={{ background: '#f44336' }}>{bugTickets.length}</span>}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="l3-body">
                {activeTab === 'Details' && (
                  <>
                    <div className="sprint-detail-section">
                      <h4>Description</h4>
                      {isInlineEditing ? (
                        <textarea className="l3-inline-textarea" value={editedTicket.detailed_description || ''} onChange={e => setEditedTicket(p => ({ ...p, detailed_description: e.target.value }))} placeholder="Add a detailed description..." rows={6} />
                      ) : (
                        <p>{selectedTicket.detailed_description || <span style={{ color: 'var(--text-subtle)' }}>No description available.</span>}</p>
                      )}
                    </div>

                    {/* Move-to-Live request / approval (shows only when the company
                        has Live Support enabled and the ticket is in a QA/UAT Pass). */}
                    <div className="sprint-detail-section">
                      <LiveMovePanel
                        ticketId={selectedTicket.id}
                        ticketStatusName={selectedTicket.status_name || selectedTicket.ticket_status}
                      />
                    </div>
                  </>
                )}

                {activeTab === 'Story & Criteria' && (() => {
                  const art = selectedTicket.artifact;
                  const artNorm = art?.normalized || {};
                  const artDevSpec = art?.dev_spec || {};

                  // User story: artifact first → ticket fallback
                  const artUS = artNorm.user_story || artNorm.user_stories || (Array.isArray(artNorm.user_flows) && artNorm.user_flows.length > 0 ? artNorm.user_flows : null);
                  const userStory = artUS || selectedTicket.user_story;

                  // Acceptance criteria: artifact (dev_spec or normalized) → ticket fallback
                  const artACList = artDevSpec.acceptance_criteria || artNorm.acceptance_criteria || [];
                  const hasArtAC = Array.isArray(artACList) && artACList.length > 0 && typeof artACList[0] === 'object';

                  return (
                  <>
                    <div className="sprint-detail-section">
                      <h4>User Story</h4>
                      {isInlineEditing ? (
                        <textarea className="l3-inline-textarea" value={editedTicket.user_story || ''} onChange={e => setEditedTicket(p => ({ ...p, user_story: e.target.value }))} placeholder="Write user story..." rows={5} />
                      ) : userStory ? (
                        typeof userStory === 'string' ? <p style={{ whiteSpace: 'pre-wrap' }}>{userStory}</p> :
                        Array.isArray(userStory) ? <ul className="l3-artifact-list" style={{ paddingLeft: 18 }}>{userStory.map((s, i) => <li key={i}>{String(s)}</li>)}</ul> :
                        <p>{String(userStory)}</p>
                      ) : <p style={{ color: 'var(--text-subtle)' }}>Not provided</p>}
                    </div>
                    <div className="sprint-detail-section">
                      <h4>Acceptance Criteria</h4>
                      {isInlineEditing ? (
                        <textarea className="l3-inline-textarea" value={editedTicket.acceptance_criteria || ''} onChange={e => setEditedTicket(p => ({ ...p, acceptance_criteria: e.target.value }))} placeholder="Write acceptance criteria..." rows={5} />
                      ) : hasArtAC ? (
                        <table className="l3-test-table">
                          <thead><tr><th>ID</th><th>Given</th><th>When</th><th>Then</th></tr></thead>
                          <tbody>
                            {artACList.filter(ac => ac && typeof ac === 'object').map((ac, i) => (
                              <tr key={ac.id || i}><td>{ac.id || i + 1}</td><td>{ac.given || '—'}</td><td>{ac.when || '—'}</td><td>{ac.then || '—'}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      ) : selectedTicket.acceptance_criteria ? (
                        <p style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.acceptance_criteria}</p>
                      ) : <p style={{ color: 'var(--text-subtle)' }}>Not provided</p>}
                    </div>
                  </>
                  );
                })()}

                {activeTab === 'Attachments' && (
                  <div className="sprint-detail-section">
                    {/* Upload area */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={(e) => setPendingFiles(Array.from(e.target.files))}
                        style={{ fontSize: '0.7rem', flex: 1, minWidth: 180 }}
                      />
                      <button
                        className="fd-btn-outline"
                        disabled={!pendingFiles.length || uploadingFiles}
                        onClick={handleUploadAttachments}
                        style={{ fontSize: '0.65rem', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        {uploadingFiles ? <CircularProgress size={12} /> : <FiUploadCloud size={12} />}
                        {uploadingFiles ? 'Uploading...' : 'Upload'}
                      </button>
                    </div>
                    {pendingFiles.length > 0 && (
                      <div style={{ marginBottom: 10, fontSize: '0.68rem', color: 'var(--text-subtle)' }}>
                        {pendingFiles.map((f, i) => <span key={i} style={{ marginRight: 8 }}>{f.name}</span>)}
                      </div>
                    )}

                    {/* Existing attachments */}
                    <h4 style={{ fontSize: '0.72rem', fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>Files</h4>
                    {ticketAttachments.length > 0 ? (
                      <div className="sprint-detail-attachments">
                        {ticketAttachments.map(att => (
                          <span key={att.id} className="sprint-detail-att-chip" style={{ cursor: 'default' }}>
                            <FiPaperclip size={11} /> {att.filename}
                            <FiDownload size={11} style={{ cursor: 'pointer' }} onClick={() => handleDownload(att)} title="Download" />
                            <FiTrash2 size={11} style={{ cursor: 'pointer', color: 'var(--danger, #e74c3c)' }} onClick={() => handleDeleteAttachment(att)} title="Delete" />
                          </span>
                        ))}
                      </div>
                    ) : <p style={{ fontSize: '0.72rem', color: 'var(--text-subtle)' }}>No attachments</p>}

                    {/* Links section */}
                    <h4 style={{ fontSize: '0.72rem', fontWeight: 600, marginTop: 16, marginBottom: 6, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      Links
                      <button className="fd-btn-outline" onClick={() => setShowAddLink(!showAddLink)} style={{ fontSize: '0.6rem', padding: '2px 6px' }}>
                        {showAddLink ? 'Cancel' : <><FiPlus size={10} /> Add Link</>}
                      </button>
                    </h4>
                    {showAddLink && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <label style={{ fontSize: '0.62rem', color: 'var(--text-subtle)', display: 'block', marginBottom: 2 }}>URL</label>
                          <input type="text" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://..." style={{ width: '100%', padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 120 }}>
                          <label style={{ fontSize: '0.62rem', color: 'var(--text-subtle)', display: 'block', marginBottom: 2 }}>Label</label>
                          <input type="text" value={linkName} onChange={(e) => setLinkName(e.target.value)} placeholder="Link name" style={{ width: '100%', padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} />
                        </div>
                        <button className="fd-btn-outline" disabled={!linkUrl || !linkName} onClick={handleAddLink} style={{ fontSize: '0.65rem', padding: '5px 10px' }}>
                          <FiCheck size={11} /> Add
                        </button>
                      </div>
                    )}
                    {selectedTicket?.links?.length > 0 ? (
                      <div className="sprint-detail-attachments">
                        {selectedTicket.links.map(link => (
                          <span key={link.id} className="sprint-detail-att-chip" style={{ cursor: 'default' }}>
                            <FiLink size={11} />
                            <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: '0.68rem' }}>{link.url_name}</a>
                            <FiTrash2 size={11} style={{ cursor: 'pointer', color: 'var(--danger, #e74c3c)' }} onClick={() => handleDeleteLink(link)} title="Delete" />
                          </span>
                        ))}
                      </div>
                    ) : <p style={{ fontSize: '0.72rem', color: 'var(--text-subtle)' }}>No links</p>}
                  </div>
                )}

                {activeTab === 'Branches' && (
                  <div className="sprint-detail-section">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <button className="fd-btn-outline" onClick={() => { setShowAddBranch(!showAddBranch); setAddBranchForm({ repository_id: '', branch_name: '', developer_id: '', branch_type: 'feature' }); }} style={{ fontSize: '0.65rem', padding: '3px 8px' }}>
                        <FiGitBranch size={11} /> {showAddBranch ? 'Cancel' : 'Add Branch'}
                      </button>
                    </div>
                    {showAddBranch && (
                      <div style={{ padding: 12, background: 'var(--bg-tertiary)', borderRadius: 8, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <select style={{ flex: 1, minWidth: 120, padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} value={addBranchForm.repository_id} onChange={(e) => setAddBranchForm(p => ({ ...p, repository_id: e.target.value }))}>
                            <option value="">Select Repository</option>
                            {gitlabRepos.filter(r => r.is_active).map(r => <option key={r.id} value={r.id}>{r.repo_name} ({r.repo_type})</option>)}
                          </select>
                          <select style={{ minWidth: 100, padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} value={addBranchForm.developer_id} onChange={(e) => setAddBranchForm(p => ({ ...p, developer_id: e.target.value }))}>
                            <option value="">Developer</option>
                            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input type="text" style={{ flex: 1, padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} value={addBranchForm.branch_name} onChange={(e) => setAddBranchForm(p => ({ ...p, branch_name: e.target.value }))} placeholder="Branch name" />
                          <select style={{ minWidth: 80, padding: '5px 8px', fontSize: '0.7rem', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--input-bg)', color: 'var(--input-color)' }} value={addBranchForm.branch_type} onChange={(e) => setAddBranchForm(p => ({ ...p, branch_type: e.target.value }))}>
                            <option value="feature">Feature</option><option value="bugfix">Bugfix</option><option value="hotfix">Hotfix</option><option value="release">Release</option><option value="other">Other</option>
                          </select>
                          <button className="fd-btn-primary" onClick={handleAddBranch} style={{ fontSize: '0.65rem', padding: '4px 10px' }}>Add</button>
                        </div>
                      </div>
                    )}
                    {ticketBranches.length > 0 ? ticketBranches.map(br => {
                      const syncData = br.last_sync_data || {};
                      const isExpBr = expandedBranchId === br.id;
                      const commits = syncData.commits || [];
                      const mrs = syncData.merge_requests || [];
                      const changedFiles = syncData.changed_files || [];
                      const fmtDT = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; } };

                      return (
                      <div key={br.id} className="l3-branch-card">
                        <div className="l3-branch-card__header">
                          <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setExpandedBranchId(isExpBr ? null : br.id)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              {isExpBr ? <FiChevronDown size={12} style={{ color: 'var(--text-muted)' }} /> : <FiChevronRight size={12} style={{ color: 'var(--text-muted)' }} />}
                              <FiGitBranch size={12} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                              <span style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-primary)' }}>{br.branch_name}</span>
                              <span className={`fd-status-pill ${br.merge_status === 'merged' ? 'fd-status-pill--success' : br.merge_status === 'open' ? 'fd-status-pill--info' : 'fd-status-pill--muted'}`}>
                                {br.merge_status === 'merged' ? 'Merged' : br.merge_status === 'open' ? 'Open MR' : br.merge_status === 'closed' ? 'Closed' : 'No MR'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.64rem', color: 'var(--text-subtle)', marginTop: 2, paddingLeft: 20 }}>
                              {br.repo_name} ({br.repo_type}) {br.developer_name ? `• ${br.developer_name}` : ''}{br.total_commits > 0 && ` • ${br.total_commits} commits`}{br.files_changed > 0 && ` • ${br.files_changed} files (+${br.additions || 0}/-${br.deletions || 0})`}{br.target_branch && br.merge_status === 'merged' && ` → ${br.target_branch}`}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                            <button className="fd-action-btn" onClick={() => handleSyncBranch(br.id)} title="Sync" disabled={branchSyncing === br.id}>{branchSyncing === br.id ? <CircularProgress size={11} /> : <FiRefreshCw size={10} />}</button>
                            <button className="fd-action-btn fd-action-btn--danger" onClick={() => handleDeleteBranch(br.id)} title="Remove"><FiTrash2 size={10} /></button>
                          </div>
                        </div>
                        {/* Expanded: Full Branch Lifecycle (matches Branch Audit Report) */}
                        {isExpBr && (() => {
                          const mainMR = mrs.length > 0 ? mrs[0] : null;
                          const fmtDTFull = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch { return d; } };

                          // Build timeline events
                          const events = [];
                          const commitDates = commits.map(c => c.date).filter(Boolean).map(d => new Date(d).getTime());
                          const earliestCommit = commitDates.length > 0 ? new Date(Math.min(...commitDates)).toISOString() : null;

                          events.push({ type: 'created', color: '#4caf50', title: `Branch created: ${br.branch_name}`, detail: `Repository: ${br.repo_name} (${br.repo_type})${br.developer_name ? ` • Developer: ${br.developer_name}` : ''}`, time: earliestCommit || br.created_at_gitlab || br.created_time });

                          commits.forEach(c => {
                            events.push({ type: 'commit', color: 'var(--accent)', title: c.message, detail: `${c.short_sha} by ${c.author}`, time: c.date });
                          });

                          if (mainMR && mainMR.created_at) {
                            events.push({ type: 'mr_opened', color: '#2196f3', title: `MR #${mainMR.iid} opened: ${mainMR.title}`, detail: `Target: ${mainMR.target_branch}${syncData.mr_reviewers?.length ? ` • Reviewers: ${syncData.mr_reviewers.join(', ')}` : ''}${syncData.mr_pipeline_status ? ` • Pipeline: ${syncData.mr_pipeline_status}` : ''}`, time: mainMR.created_at });
                          }

                          if (br.merge_status === 'merged' && br.merged_at) {
                            events.push({ type: 'merged', color: '#4caf50', title: `Merged into ${br.target_branch}`, detail: `Merged by ${br.merged_by || 'unknown'}${br.files_changed > 0 ? ` • ${br.files_changed} files changed (+${br.additions}/-${br.deletions})` : ''}`, time: br.merged_at });
                          }

                          if (br.merge_status === 'closed') {
                            events.push({ type: 'closed', color: '#f44336', title: 'MR closed without merge', detail: `Target was: ${br.target_branch || '-'}`, time: br.last_synced_at });
                          }

                          events.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));

                          return (
                          <div className="l3-branch-audit">
                            {/* Stats bar — matching audit report */}
                            <div className="l3-branch-stats">
                              <div><span className="l3-branch-stats__label">Status</span><span className="l3-branch-stats__val"><span className={`fd-status-pill ${br.merge_status === 'merged' ? 'fd-status-pill--success' : br.merge_status === 'open' ? 'fd-status-pill--info' : 'fd-status-pill--muted'}`}>{br.merge_status === 'merged' ? 'Merged' : br.merge_status === 'open' ? 'Open MR' : br.merge_status === 'closed' ? 'Closed' : 'No MR'}</span></span></div>
                              <div><span className="l3-branch-stats__label">Commits</span><span className="l3-branch-stats__val">{br.total_commits || 0}</span></div>
                              <div><span className="l3-branch-stats__label">Files</span><span className="l3-branch-stats__val">{br.files_changed > 0 ? `${br.files_changed} (+${br.additions || 0}/-${br.deletions || 0})` : '-'}</span></div>
                              {br.target_branch && <div><span className="l3-branch-stats__label">Target Branch</span><span className="l3-branch-stats__val" style={{ color: '#4caf50' }}>{br.target_branch}</span></div>}
                              {mainMR && <div><span className="l3-branch-stats__label">MR</span><span className="l3-branch-stats__val">#{mainMR.iid} — {mainMR.title || '-'}</span></div>}
                              <div><span className="l3-branch-stats__label">Last Synced</span><span className="l3-branch-stats__val">{fmtDTFull(br.last_synced_at)}</span></div>
                            </div>

                            {/* Branch Lifecycle Timeline */}
                            <h5 className="l3-audit-title"><FiClock size={11} /> Branch Lifecycle Timeline ({events.length} events)</h5>
                            <div className="l3-timeline">
                              {events.map((evt, ei) => (
                                <div key={ei} className="l3-timeline__item">
                                  <div className="l3-timeline__dot-col">
                                    <div className="l3-timeline__dot" style={{ background: evt.color }} />
                                    {ei < events.length - 1 && <div className="l3-timeline__line" />}
                                  </div>
                                  <div className="l3-timeline__content">
                                    <div className="l3-timeline__title">{evt.title}</div>
                                    {evt.detail && <div className="l3-timeline__detail">{evt.detail}</div>}
                                  </div>
                                  <div className="l3-timeline__time">{fmtDTFull(evt.time)}</div>
                                </div>
                              ))}
                            </div>

                            {/* Changed files */}
                            {changedFiles.length > 0 && (
                              <>
                                <h5 className="l3-audit-title"><FiFileText size={11} /> Changed Files ({changedFiles.length})</h5>
                                <div className="l3-files-list">
                                  {changedFiles.map((f, fi) => (
                                    <div key={fi} className="l3-file-row">
                                      <span className={`l3-file-badge ${f.new_file ? 'l3-file-badge--add' : f.deleted ? 'l3-file-badge--del' : f.renamed ? 'l3-file-badge--ren' : 'l3-file-badge--mod'}`}>
                                        {f.new_file ? 'A' : f.deleted ? 'D' : f.renamed ? 'R' : 'M'}
                                      </span>
                                      <span className="l3-file-path">{f.new_path || f.old_path}</span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                          );
                        })()}
                      </div>
                      );
                    }) : <p style={{ fontSize: '0.72rem', color: 'var(--text-subtle)' }}>No branches tracked</p>}
                  </div>
                )}

                {/* ── Stories tab (Epic tickets) ── */}
                {activeTab === 'Stories' && isEpic && (
                  <div className="l3-child-tickets">
                    <div className="l3-child-tickets__header">
                      <h4><FiGitBranch size={13} /> Story Tickets ({childTickets.length})</h4>
                    </div>
                    {childTickets.map(child => {
                      const cStatus = child.status_name || child.ticket_status || '-';
                      const cCode = child.child_code || child.parent_code;
                      return (
                        <div key={child.id} className="l3-child-ticket" onClick={() => handleChildTicketClick(child, 'Stories')}>
                          <span className="sl2-type-badge sl2-type-badge--story">Story</span>
                          <span className="l3-child-ticket__code">{cCode}</span>
                          <span className="l3-child-ticket__desc">{child.short_description || '-'}</span>
                          <span className={`fd-status-pill ${/done|completed|live|production/i.test(cStatus) ? 'fd-status-pill--success' : /progress|dev|qa|review/i.test(cStatus) ? 'fd-status-pill--info' : /fail|reject|block/i.test(cStatus) ? 'fd-status-pill--danger' : 'fd-status-pill--muted'}`}>{cStatus}</span>
                          <span className="l3-child-ticket__prio">{child.priority_name || '-'}</span>
                          {child.bug_tickets?.length > 0 && <span className="sl2-bug-chip"><GoBug size={10} /> {child.bug_tickets.length}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Bugs tab (Story tickets) ── */}
                {activeTab === 'Bugs' && isStory && (
                  <div className="l3-child-tickets">
                    <div className="l3-child-tickets__header">
                      <h4><GoBug size={13} /> Bug Tickets ({bugTickets.length})</h4>
                    </div>
                    {bugTickets.map(bug => {
                      const bStatus = bug.status_name || bug.ticket_status || '-';
                      const bCode = bug.bug_code;
                      return (
                        <div key={bug.id} className="l3-child-ticket l3-child-ticket--bug" onClick={() => handleChildTicketClick(bug, 'Bugs')}>
                          <span className="sl2-type-badge sl2-type-badge--bug">Bug</span>
                          <span className="l3-child-ticket__code">{bCode}</span>
                          <span className="l3-child-ticket__desc">{bug.short_description || '-'}</span>
                          <span className={`fd-status-pill ${/done|completed|live|production/i.test(bStatus) ? 'fd-status-pill--success' : /progress|dev|qa|review/i.test(bStatus) ? 'fd-status-pill--info' : /fail|reject|block/i.test(bStatus) ? 'fd-status-pill--danger' : 'fd-status-pill--muted'}`}>{bStatus}</span>
                          {bug.bug_type && <span className="l3-child-ticket__bugtype">{bug.bug_type}</span>}
                          <span className="l3-child-ticket__prio">{bug.priority_name || '-'}</span>
                          <button
                            className="fd-action-btn fd-action-btn--danger"
                            title="Delete bug"
                            onClick={e => { e.stopPropagation(); handleDeleteTicket(bug.id); }}
                          >
                            <FiTrash2 size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Artifact sub-tabs: Normalized, Dev Spec, QA Plan ── */}
                {hasArtifact && (() => {
                  const art = selectedTicket.artifact;

                  // Render a list of items (strings or objects)
                  const renderList = (arr) => {
                    if (!arr || !Array.isArray(arr) || arr.length === 0) return <span style={{ color: 'var(--text-subtle)' }}>—</span>;
                    return (
                      <ul className="l3-artifact-list">
                        {arr.map((item, i) => <li key={i}>{typeof item === 'object' ? (item.given ? `Given ${item.given}, When ${item.when}, Then ${item.then}` : Object.values(item).join(' — ')) : String(item)}</li>)}
                      </ul>
                    );
                  };

                  // Render sections: skip test_cases/unit_tests (handled separately as tables)
                  const renderSections = (data, skipKeys = []) => {
                    if (!data || typeof data !== 'object') return <p style={{ color: 'var(--text-subtle)', fontSize: '0.78rem' }}>No data available.</p>;
                    return Object.entries(data).filter(([k]) => !skipKeys.includes(k)).map(([key, val]) => (
                      <div key={key} className="l3-artifact-section">
                        <h4 className="l3-artifact-section__title">{key.replace(/_/g, ' ')}</h4>
                        <div style={{ padding: '10px 16px' }}>
                          {Array.isArray(val) ? renderList(val) : typeof val === 'object' && val ? (
                            Object.entries(val).map(([k2, v2]) => (
                              <div key={k2} className="l3-artifact-item">
                                <span className="l3-artifact-item__key">{k2.replace(/_/g, ' ')}</span>
                                <div className="l3-artifact-item__val">{Array.isArray(v2) ? renderList(v2) : <span>{String(v2)}</span>}</div>
                              </div>
                            ))
                          ) : <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>{String(val)}</span>}
                        </div>
                      </div>
                    ));
                  };

                  // Test cases table renderer (for both QA test cases and unit tests)
                  const renderTestTable = (cases, title) => {
                    if (!cases || !Array.isArray(cases) || cases.length === 0) return null;
                    // Detect columns from first item
                    const cols = Object.keys(cases[0]).filter(k => !['test_data', 'maps_to_acceptance_criteria'].includes(k));
                    return (
                      <div className="l3-artifact-section">
                        <h4 className="l3-artifact-section__title">{title} ({cases.length})</h4>
                        <div style={{ overflowX: 'auto' }}>
                          <table className="l3-test-table">
                            <thead>
                              <tr>{cols.map(c => <th key={c}>{c.replace(/_/g, ' ')}</th>)}</tr>
                            </thead>
                            <tbody>
                              {cases.map((tc, i) => (
                                <tr key={i}>
                                  {cols.map(c => (
                                    <td key={c}>
                                      {Array.isArray(tc[c]) ? tc[c].join(', ') : typeof tc[c] === 'object' && tc[c] ? JSON.stringify(tc[c]) : String(tc[c] ?? '—')}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  };

                  if (activeTab === 'Normalized') {
                    const norm = art.normalized;
                    if (!norm) return <p style={{ color: 'var(--text-subtle)', fontSize: '0.78rem' }}>No data available.</p>;
                    return renderSections(norm, ['test_cases', 'unit_tests']);
                  }

                  if (activeTab === 'Dev Spec') {
                    const spec = art.dev_spec;
                    if (!spec) return <p style={{ color: 'var(--text-subtle)', fontSize: '0.78rem' }}>No data available.</p>;
                    const unitTestsObj = spec.unit_tests || art.unit_tests;
                    // unit_tests can be: { unit_test_cases: [...], test_framework_suggestion: "..." } or just an array
                    const unitTestCases = Array.isArray(unitTestsObj) ? unitTestsObj : (unitTestsObj?.unit_test_cases || unitTestsObj?.test_cases || unitTestsObj?.cases || []);
                    const testFramework = unitTestsObj?.test_framework_suggestion || null;
                    return (
                      <>
                        {renderSections(spec, ['unit_tests', 'acceptance_criteria'])}
                        {unitTestCases.length > 0 && (
                          <div className="l3-artifact-section">
                            <h4 className="l3-artifact-section__title">Unit Tests ({unitTestCases.length}){testFramework && <span style={{ fontWeight: 400, fontSize: '0.65rem', marginLeft: 8, color: 'var(--text-muted)' }}>Framework: {testFramework}</span>}</h4>
                            <div style={{ overflowX: 'auto' }}>
                              <table className="l3-test-table">
                                <thead>
                                  <tr><th>ID</th><th>Title</th><th>Priority</th><th>Target</th><th>Description</th><th>Expected Behavior</th><th>Assertions</th></tr>
                                </thead>
                                <tbody>
                                  {unitTestCases.filter(ut => ut && typeof ut === 'object').map((ut, i) => (
                                    <tr key={ut.id || i}>
                                      <td>{ut.id || i + 1}</td>
                                      <td>{ut.title || '—'}</td>
                                      <td>{ut.priority || '—'}</td>
                                      <td>{ut.target || '—'}</td>
                                      <td>{ut.description || '—'}</td>
                                      <td>{ut.expected_behavior || '—'}</td>
                                      <td>{Array.isArray(ut.assertions) ? ut.assertions.join(', ') : (ut.assertions || '—')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  }

                  if (activeTab === 'QA Plan') {
                    const qa = art.qa_plan;
                    if (!qa) return <p style={{ color: 'var(--text-subtle)', fontSize: '0.78rem' }}>No data available.</p>;
                    const testCases = Array.isArray(qa.test_cases) ? qa.test_cases : (Array.isArray(qa.cases) ? qa.cases : []);
                    return (
                      <>
                        {renderSections(qa, ['test_cases', 'cases'])}
                        {testCases.length > 0 && (
                          <div className="l3-artifact-section">
                            <h4 className="l3-artifact-section__title">Test Cases ({testCases.length})</h4>
                            <div style={{ overflowX: 'auto' }}>
                              <table className="l3-test-table">
                                <thead>
                                  <tr><th>ID</th><th>Title</th><th>Type</th><th>Priority</th><th>Preconditions</th><th>Steps</th><th>Expected Results</th></tr>
                                </thead>
                                <tbody>
                                  {testCases.filter(tc => tc && typeof tc === 'object').map((tc, i) => (
                                    <tr key={tc.id || i}>
                                      <td>{tc.id || i + 1}</td>
                                      <td>{tc.title || '—'}</td>
                                      <td><span className={`l3-tc-type ${tc.type === 'positive' ? 'l3-tc-type--pos' : tc.type === 'negative' ? 'l3-tc-type--neg' : ''}`}>{tc.type || '—'}</span></td>
                                      <td>{tc.priority || '—'}</td>
                                      <td>{Array.isArray(tc.preconditions) ? tc.preconditions.join('; ') : (tc.preconditions || '—')}</td>
                                      <td>{Array.isArray(tc.steps) ? <ol style={{ margin: 0, paddingLeft: 16 }}>{tc.steps.map((s, si) => <li key={si}>{s}</li>)}</ol> : (tc.steps || '—')}</td>
                                      <td>{Array.isArray(tc.expected_results) ? tc.expected_results.join('; ') : (tc.expected_results || '—')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  }

                  return null;
                })()}
              </div>
            </div>

            {/* Comments & Timeline Panel */}
            <CommentsPanel
              comments={ticketComments}
              loading={commentsLoading}
              onAddComment={handleAddComment}
              employees={employees}
              statusLog={selectedTicket.status_log || []}
              ticketCreatedTime={selectedTicket.created_time}
              ticketCreatedBy={selectedTicket.created_by}
              branches={ticketBranches}
            />
          </div>
        </>
        );
      })()}

      {/* ── Context Menu ── */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeContextMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {/* Hierarchy: Epic → Story only · Story → Bug only · Bug → no children */}
        {menuTicket?.ticket_type === 'Epic' && (
          <MenuItem sx={{ fontSize: '0.75rem' }} onClick={() => openAddTicketPopup(menuTicket, 'Story')}><FiPlus size={12} style={{ marginRight: 6 }} />Add Story</MenuItem>
        )}
        {menuTicket?.ticket_type === 'Story' && (
          <MenuItem sx={{ fontSize: '0.75rem' }} onClick={() => openAddTicketPopup(menuTicket, 'Bug')}><GoBug size={12} style={{ marginRight: 6 }} />Add Bug</MenuItem>
        )}
        {(menuTicket?.ticket_type === 'Epic' || menuTicket?.story_type === 'Parent') && (
          <MenuItem sx={{ fontSize: '0.75rem' }} onClick={() => { setMovePopup(true); closeContextMenu(); }}><FiGitMerge size={12} style={{ marginRight: 6 }} />Move to Sprint</MenuItem>
        )}
        {liveSupportEnabled && selectedSprint?.is_live_support && userPermission?.includes('Request Live Move') && (
          <MenuItem sx={{ fontSize: '0.75rem' }} onClick={() => handleSendToLive(menuTicket)}><FiGitMerge size={12} style={{ marginRight: 6 }} />Send to Live</MenuItem>
        )}
        {userPermission?.includes('Delete Ticket') && (
          <MenuItem sx={{ fontSize: '0.75rem', color: 'var(--error, #e53935)' }} onClick={() => { handleDeleteTicket(menuTicket?.id); closeContextMenu(); }}><FiTrash2 size={12} style={{ marginRight: 6 }} />Delete</MenuItem>
        )}
      </Menu>

      {/* ── Detailed View Modal ── */}
      {showDetailedView && detailViewTicket && (
        <DetailedTicketView
          onClose={() => { setShowDetailedView(false); setDetailViewTicket(null); }}
          ticketId={detailViewTicket.id}
        />
      )}

      {/* ── Move to Sprint Modal ── */}
      {movePopup && (
        <div className="popup-overlay">
          <div className="popup-content" style={{ maxWidth: 400 }}>
            <h3 style={{ marginBottom: 12 }}>Move Ticket to Sprint</h3>
            <select className="fd-search" style={{ width: '100%', maxWidth: 'none', marginBottom: 12 }} value={moveTarget || ''} onChange={(e) => setMoveTarget(e.target.value)}>
              <option value="" disabled>Select Sprint</option>
              {allSprints.filter(s => s.id !== selectedSprint?.id).map(s => <option key={s.id} value={s.id}>{s.sprint_name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="fd-btn-outline" onClick={() => { setMovePopup(false); setMenuTicket(null); }}>Cancel</button>
              <button className="fd-btn-primary" onClick={handleMoveTicket} disabled={!moveTarget}>Move</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Child/Bug Ticket Popup (Modern) ── */}
      {addTicketPopup && (
        <div className="sl2-qc-overlay" onClick={() => setAddTicketPopup(false)}>
          <div className="sl2-qc-modal" onClick={e => e.stopPropagation()}>
            <div className="sl2-qc-modal__header">
              <h2>Add {addTicketType} <span style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--text-muted)' }}>{addTicketParent ? `${addTicketType === 'Story' ? 'under' : 'for'} ${addTicketParent.parent_code || addTicketParent.child_code}` : `in ${selectedSprint?.sprint_name || 'this sprint'}`}</span></h2>
              <button className="sl2-qc-modal__close" onClick={() => setAddTicketPopup(false)}><FiX size={18} /></button>
            </div>
            <div className="sl2-qc-modal__body">
              <div className="sl2-qc-field">
                <label>Title</label>
                <input type="text" placeholder={`What does this ${addTicketType.toLowerCase()} cover?`} value={addTicketForm.short_description} onChange={(e) => setAddTicketForm(p => ({ ...p, short_description: e.target.value }))} autoFocus />
              </div>
              <div className="sl2-qc-row">
                {!addTicketParent && (
                  <div className="sl2-qc-field">
                    <label>Type</label>
                    <div className="sl2-qc-type-group">
                      {['Epic', 'Story'].map(tt => (
                        <button key={tt} className={`sl2-qc-type-btn ${addTicketType === tt ? 'sl2-qc-type-btn--active' : ''}`} onClick={() => setAddTicketType(tt)}>{tt}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="sl2-qc-field">
                  <label>Priority</label>
                  <div className="sl2-qc-prio-group">
                    {priorityTypes.filter(p => p.is_active).map(p => (
                      <button key={p.id} className={`sl2-qc-prio-btn ${String(addTicketForm.priority_id) === String(p.id) ? 'sl2-qc-prio-btn--active' : ''}`} onClick={() => setAddTicketForm(prev => ({ ...prev, priority_id: prev.priority_id === p.id ? '' : p.id }))}>{p.priority}</button>
                    ))}
                  </div>
                </div>
                <div className="sl2-qc-field">
                  <label>Due Date <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                  <input type="date" value={addTicketForm.due_date || ''} onChange={e => setAddTicketForm(p => ({ ...p, due_date: e.target.value }))} />
                </div>
                {addTicketType === 'Bug' && (
                  <div className="sl2-qc-field">
                    <label>Bug Type</label>
                    <div className="sl2-qc-type-group">
                      {['Development', 'Production', 'Gap'].map(bt => (
                        <button key={bt} className={`sl2-qc-type-btn ${addTicketForm.bug_type === bt ? 'sl2-qc-type-btn--active sl2-qc-type-btn--bug' : ''}`} onClick={() => setAddTicketForm(p => ({ ...p, bug_type: bt }))}>{bt}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="sl2-qc-status-info">
                <span className="sl2-qc-status-dot" />Status will be set to <strong>To Do</strong>
              </div>
            </div>
            <div className="sl2-qc-modal__footer">
              <button className="sl2-qc-cancel" onClick={() => setAddTicketPopup(false)}>Cancel</button>
              <button className="sl2-qc-submit" onClick={handleSaveNewTicket}>
                {addTicketType === 'Bug' ? <><GoBug size={13} /> Create Bug</> : <><FiPlus size={13} /> Create {addTicketType}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sprint Add/Edit Popup ── */}
      {showSprintPopup && (
        <div className="popup-overlay">
          <div className="popup-content-req" style={{ maxWidth: 560 }}>
            <h3>{editSprintId ? 'Edit Sprint' : 'Add Sprint'}</h3>
            <div className="req-popup-form">
              <div className="req-popup-field full-width">
                <label>Sprint Name</label>
                <input type="text" value={sprintForm.sprint_name} onChange={(e) => setSprintForm(p => ({ ...p, sprint_name: e.target.value }))} placeholder="Sprint name" />
              </div>
              <div className="req-popup-field">
                <label>Short Name</label>
                <input type="text" value={sprintForm.short_name} onChange={(e) => setSprintForm(p => ({ ...p, short_name: e.target.value }))} placeholder="e.g. DEC1" />
              </div>
              <div className="req-popup-field">
                <label>Team Name</label>
                <input type="text" value={sprintForm.team_name} onChange={(e) => setSprintForm(p => ({ ...p, team_name: e.target.value }))} placeholder="Team name" />
              </div>
              <div className="req-popup-field">
                <label>Start Date</label>
                <input type="date" value={sprintForm.start_date} onChange={(e) => setSprintForm(p => ({ ...p, start_date: e.target.value }))} />
              </div>
              <div className="req-popup-field">
                <label>End Date</label>
                <input type="date" value={sprintForm.end_date} onChange={(e) => setSprintForm(p => ({ ...p, end_date: e.target.value }))} />
              </div>
              <div className="req-popup-field">
                <label>Status</label>
                <select
                  value={sprintForm.sprint_status || 'ONGOING'}
                  onChange={(e) => setSprintForm(p => ({ ...p, sprint_status: e.target.value }))}
                >
                  {SPRINT_STATUS_OPTIONS.filter(o => o.value !== 'all').map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="req-popup-field full-width">
                <label>Team Members</label>
                <Autocomplete
                  multiple size="small"
                  options={employees}
                  getOptionLabel={(opt) => opt.name || ''}
                  value={employees.filter(e => (sprintForm.team_members || []).includes(e.id))}
                  onChange={(_, val) => setSprintForm(p => ({ ...p, team_members: val.map(v => v.id) }))}
                  renderInput={(params) => <TextField {...params} placeholder="Select members" size="small" />}
                />
              </div>
              {liveSupportEnabled && (
                <div className="req-popup-field full-width">
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={!!sprintForm.is_live_support}
                      onChange={(e) => setSprintForm(p => ({ ...p, is_live_support: e.target.checked }))}
                      style={{ width: "16px", height: "16px" }}
                    />
                    Live Support Team Sprint
                  </label>
                </div>
              )}
            </div>
            <div className="req-popup-actions">
              <button className="fd-btn-outline" onClick={() => { setShowSprintPopup(false); setEditSprintId(null); }}>Cancel</button>
              <button className="fd-btn-primary" onClick={handleSaveSprint}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Side Panel (existing TicketPanel) ── */}
      {isSidebarOpen && (
        <TicketSidePanel
          editData={editData}
          closePanel={handleSidePanelClose}
          sprintId={selectedSprint?.id}
          refreshData={() => setRefreshTrigger(p => !p)}
        />
      )}
    </div>
  );
};

/* ── Simple comment input component ── */
/* ── Comments Panel with Reply Support ── */
const CommentsPanel = ({ comments, loading, onAddComment, employees, statusLog = [], ticketCreatedTime, ticketCreatedBy, branches = [] }) => {
  const [panelTab, setPanelTab] = useState('comments');
  const [replyingTo, setReplyingTo] = useState(null);
  const commentsList = Object.values(comments || {});
  const avatarColors = ['#7c5cfc','#1976d2','#e53935','#43a047','#f5a623','#8e24aa','#00897b','#00acc1'];
  const getColor = (name) => avatarColors[(name || '?').charCodeAt(0) % avatarColors.length];
  const fmtDT = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; } };

  // Build timeline events
  const timelineEvents = React.useMemo(() => {
    const events = [];
    // Ticket created
    if (ticketCreatedTime) {
      events.push({ type: 'created', icon: '🎫', title: 'Ticket created', detail: ticketCreatedBy ? `by ${ticketCreatedBy}` : '', time: ticketCreatedTime, color: '#4caf50' });
    }
    // Status changes
    (statusLog || []).forEach(log => {
      if (log.from_status && log.to_status) {
        events.push({ type: 'status', icon: '🔄', title: `Status changed`, detail: `${log.from_status} → ${log.to_status}`, time: log.changed_date, color: '#2196f3', by: log.changed_by });
      } else if (log.to_status) {
        events.push({ type: 'status', icon: '📋', title: `Status set to ${log.to_status}`, detail: '', time: log.changed_date, color: '#7c5cfc', by: log.changed_by });
      }
    });
    // Comments
    commentsList.forEach(c => {
      events.push({ type: 'comment', icon: '💬', title: `${c.commented_by_name || 'Someone'} commented`, detail: (c.comments || '').replace(/<[^>]*>/g, '').slice(0, 80), time: c.created_time, color: '#ff9800' });
    });
    // Branch events
    (branches || []).forEach(br => {
      const syncData = br.last_sync_data || {};
      const commits = syncData.commits || [];
      const mrs = syncData.merge_requests || [];

      // Branch created
      const earliestCommit = commits.map(c => c.date).filter(Boolean).sort()[0];
      events.push({ type: 'branch', icon: '🌿', title: `Branch created: ${br.branch_name}`, detail: `${br.repo_name} (${br.repo_type})${br.developer_name ? ` • ${br.developer_name}` : ''}`, time: earliestCommit || br.created_at_gitlab || br.created_time, color: '#00bcd4' });

      // Commits
      commits.forEach(c => {
        events.push({ type: 'commit', icon: '📝', title: c.message, detail: `${c.short_sha} by ${c.author}`, time: c.date, color: '#8e24aa' });
      });

      // MR opened
      if (mrs[0]?.created_at) {
        events.push({ type: 'mr', icon: '🔀', title: `MR #${mrs[0].iid} opened: ${mrs[0].title}`, detail: `Target: ${mrs[0].target_branch}`, time: mrs[0].created_at, color: '#3f51b5' });
      }

      // Merged
      if (br.merge_status === 'merged' && br.merged_at) {
        events.push({ type: 'merged', icon: '✅', title: `Merged into ${br.target_branch}`, detail: `by ${br.merged_by || 'unknown'}${br.files_changed ? ` • ${br.files_changed} files` : ''}`, time: br.merged_at, color: '#4caf50' });
      }

      // MR closed
      if (br.merge_status === 'closed') {
        events.push({ type: 'closed', icon: '❌', title: 'MR closed without merge', detail: `Target: ${br.target_branch || '-'}`, time: br.last_synced_at, color: '#f44336' });
      }
    });

    events.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
    return events;
  }, [statusLog, ticketCreatedTime, ticketCreatedBy, commentsList, branches]);

  const renderComment = (c, isSub = false) => (
    <div key={c.id} className={`l3-comment ${isSub ? 'l3-comment--sub' : ''}`}>
      <div className="l3-comment__head">
        <div className={`l3-comment__avatar ${isSub ? 'l3-comment__avatar--sm' : ''}`} style={{ background: getColor(c.commented_by_name) }}>
          {(c.commented_by_name || '?')[0].toUpperCase()}
        </div>
        <div className="l3-comment__info">
          <span className="l3-comment__name">{c.commented_by_name || 'Unknown'}</span>
          <span className="l3-comment__time">{c.time_ago || c.created_time || ''}</span>
        </div>
      </div>
      <div className="l3-comment__body" dangerouslySetInnerHTML={{ __html: c.comments || '' }} />
      {!isSub && (
        <button className="l3-comment__reply-btn" onClick={() => setReplyingTo(replyingTo === c.id ? null : c.id)}>
          {replyingTo === c.id ? 'Cancel' : 'Reply'}
        </button>
      )}
      {c.sub_comments?.length > 0 && (
        <div className="l3-sub-comments">
          {c.sub_comments.map(sub => renderComment(sub, true))}
        </div>
      )}
      {replyingTo === c.id && (
        <div className="l3-reply-input">
          <CommentInput onSubmit={(text) => { onAddComment(text, c.id); setReplyingTo(null); }} employees={employees} placeholder={`Reply to ${c.commented_by_name || 'comment'}...`} compact />
        </div>
      )}
    </div>
  );

  return (
    <div className="l3-comments">
      {/* Tab switcher */}
      <div className="l3-panel-tabs">
        <button className={`l3-panel-tab ${panelTab === 'comments' ? 'l3-panel-tab--active' : ''}`} onClick={() => setPanelTab('comments')}>
          Comments <span className="l3-panel-tab__count">{commentsList.length}</span>
        </button>
        <button className={`l3-panel-tab ${panelTab === 'timeline' ? 'l3-panel-tab--active' : ''}`} onClick={() => setPanelTab('timeline')}>
          Timeline <span className="l3-panel-tab__count">{timelineEvents.length}</span>
        </button>
      </div>

      {/* Comments view */}
      {panelTab === 'comments' && (
        <div className="l3-comments__list">
          {loading ? <CircularProgress size={20} /> : (
            commentsList.length > 0 ? commentsList.map(c => renderComment(c)) : <p className="l3-comments__empty">No comments yet</p>
          )}
          <div className="l3-comments__input-inline">
            <CommentInput onSubmit={(text) => onAddComment(text)} employees={employees} />
          </div>
        </div>
      )}

      {/* Timeline view */}
      {panelTab === 'timeline' && (
        <div className="l3-timeline-panel">
          {timelineEvents.length > 0 ? timelineEvents.map((evt, i) => (
            <div key={i} className={`l3-tl-item l3-tl-item--${evt.type}`}>
              <div className="l3-tl-dot-col">
                <div className="l3-tl-dot" style={{ background: evt.color }}><span className="l3-tl-icon">{evt.icon}</span></div>
                {i < timelineEvents.length - 1 && <div className="l3-tl-line" />}
              </div>
              <div className="l3-tl-content">
                <div className="l3-tl-title">{evt.title}</div>
                {evt.detail && <div className="l3-tl-detail">{evt.detail}</div>}
                {evt.by && <div className="l3-tl-by">by {evt.by}</div>}
              </div>
              <div className="l3-tl-time">{fmtDT(evt.time)}</div>
            </div>
          )) : <p className="l3-comments__empty">No activity yet</p>}
        </div>
      )}
    </div>
  );
};

const CommentInput = ({ onSubmit, employees = [], placeholder, compact }) => {
  const [text, setText] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [cursorPos, setCursorPos] = useState(0);
  const textRef = React.useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    setText(val);
    setCursorPos(pos);

    // Detect @ trigger
    const textBefore = val.slice(0, pos);
    const atIdx = textBefore.lastIndexOf('@');
    if (atIdx >= 0 && (atIdx === 0 || textBefore[atIdx - 1] === ' ' || textBefore[atIdx - 1] === '\n')) {
      const query = textBefore.slice(atIdx + 1);
      if (!query.includes(' ') && query.length < 20) {
        setMentionSearch(query.toLowerCase());
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  const insertMention = (name) => {
    const textBefore = text.slice(0, cursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    const before = text.slice(0, atIdx);
    const after = text.slice(cursorPos);
    const newText = `${before}@${name} ${after}`;
    setText(newText);
    setShowMentions(false);
    setTimeout(() => {
      if (textRef.current) {
        const newPos = atIdx + name.length + 2;
        textRef.current.focus();
        textRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const filteredEmps = employees.filter(e => e.is_active !== false && (e.name || '').toLowerCase().includes(mentionSearch));

  return (
    <div className="l3-comment-input-wrap">
      <div style={{ position: 'relative' }}>
        <textarea ref={textRef} className={`l3-comment-textarea ${compact ? 'l3-comment-textarea--compact' : ''}`} value={text} onChange={handleChange} placeholder={placeholder || "Add a comment... (type @ to mention)"} rows={compact ? 1 : 2} />
        {showMentions && filteredEmps.length > 0 && (
          <div className="l3-mention-popup">
            {filteredEmps.slice(0, 6).map(emp => (
              <div key={emp.id} className="l3-mention-item" onClick={() => insertMention(emp.name)}>
                <span className="l3-mention-avatar">{(emp.name || '?')[0].toUpperCase()}</span>
                <span>{emp.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="l3-comment-send-row">
        <span className="l3-comment-hint">@ to mention</span>
        <button className="cr-btn cr-btn--primary cr-btn--sm" onClick={() => { onSubmit(text); setText(""); }} disabled={!text.trim()}>Send</button>
      </div>
    </div>
  );
};

export default SprintDashboard;
